import { useState, useEffect, useRef, useCallback } from "react";
import { MODULES, CATEGORIES, PASS_SCORE } from "./data/modules";

/* ============================================================ THE LEGACY 36-MODULE COURSE
   SUPERSEDED 2026-08-19 by Randall's ruling: HIDE, DO NOT DELETE.

   HG Education (`view === "hg-education"`) is the course. This was the system before it, and
   it is now unreachable: the admin nav item is gone and the two views it fed -- the slide
   viewer and its quiz -- can no longer be opened, because only its dashboard ever set
   `currentModule`.

   NOTHING IS DELETED. `src/data/modules.js`, `<Dashboard/>`, `<SlideViewer/>` and `<Quiz/>` all
   remain, and flipping this one constant brings the whole thing back.

   THERE WAS NO DATABASE DATA TO VOID, AND THAT WAS CHECKED RATHER THAN ASSUMED. Measured
   2026-08-19 against production: no `modules`, `slide_videos`, `module_progress` or `progress`
   collection exists; `certificates` exists with 0 documents; 0 users have a non-empty
   `completed_modules` and 0 have a non-empty `quiz_scores`. Nobody has any work stored here,
   which is what made hiding it safe. The 36 modules only ever lived in the frontend constant
   file, so the file IS the data and it survives untouched. */
const LEGACY_MODULES_VOIDED = true;

import "./App.css";

// Lucide icons — the Hub's icon set (audit item #10: one icon system, HG green)
import {
  BarChart3, Sparkles, Users, Target, Calendar, MapPin, Megaphone,
  MessagesSquare, Wrench, CreditCard, GraduationCap, Settings as SettingsIcon,
  Award, Home, LogOut, Bell, CircleUser, DollarSign, UserPlus, Menu,
  Trophy, BookOpen, AlertTriangle, ClipboardList, CalendarPlus, Lock,
} from "lucide-react";

// Import new components
import { CoachRoundTable } from "./components/CoachRoundTable";
import { FittingSystemView } from "./components/FittingSystemView";
import { LicenseFeeView } from "./components/LicenseFeeView";
import { OnboardingFlow } from "./components/OnboardingFlow";
import { PaymentsView } from "./components/PaymentsView";
import { Education } from "./components/Education";
import BrandMarketing from "./components/BrandMarketing";
import BrandGuidelines from "./components/BrandGuidelines";
import PilotTracker from "./components/PilotTracker";
import PilotOptInCard from "./components/PilotOptInCard";
import MailCampaigns from "./components/MailCampaigns";
import PgaSubmissionPanel from "./components/PgaSubmissionPanel";
import TrainingStatus from "./components/TrainingStatus";
import { SubmissionsWaitingBanner } from "./components/PgaSubmissionPanel";
import ErrorBoundary from "./ErrorBoundary";
import { certificateState } from "./lib/certificateState";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Initials for a customer avatar, from a record that may be incomplete.
 *
 * Was `{golfer.first_name[0]}{golfer.last_name[0]}` inline. A record without a first_name threw
 * and, with no error boundary, white-screened the whole application. Module scope so the rule
 * has ONE owner rather than being re-derived at each place a name is abbreviated.
 */
function initialsOf(person) {
  const first = String(person?.first_name || "").trim();
  const last = String(person?.last_name || "").trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return initials || "?";
}

// AUDIT FIX #8: marketing template PREVIEWS render with friendly example values —
// the Copy button still hands the fitter the raw template with {placeholders}.
const TEMPLATE_EXAMPLE_VALUES = {
  facility_name: "Pine Ridge Golf Club", fitter_name: "Alex Morgan",
  golfer_name: "Jordan Lee", event_date: "Saturday, June 14",
  start_time: "9:00 AM", end_time: "4:00 PM", contact_name: "Alex",
  yards: "12", recommendations: "a 1° upright lie and a softer shaft",
  improvement_area: "strike consistency",
};
const fillTemplateExample = (t) =>
  (t || "").replace(/\{([a-z_]+)\}/g, (m, k) => TEMPLATE_EXAMPLE_VALUES[k] || m);

// Helper function to format bold text
function formatContent(text) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

// Category icons
function getCategoryIcon(cat) {
  const icons = {
    "Company Overview": "HG",
    "Club Fitting Fundamentals": "CF",
    "Lie Angle": "LA",
    "Club Length": "CL",
    "Shaft Fitting": "SF",
    "Loft & Head Design": "LH",
    "Clubfitting Model": "CM",
    "Swing Analysis": "SA",
    "Sales & Customer Skills": "SC",
    "Operations": "OP",
  };
  return icons[cat] || "M";
}

// CoFitter navigation icons — Lucide (the Hub's set), one consistent stroke.
function CoPilotIcon({ type, size = 20 }) {
  const iconProps = { size, strokeWidth: 1.7 };
  const icons = {
    dashboard: <BarChart3 {...iconProps} />,
    randy: <Sparkles {...iconProps} />,
    crm: <Users {...iconProps} />,
    fitting: <Target {...iconProps} />,
    events: <Calendar {...iconProps} />,
    territory: <MapPin {...iconProps} />,
    marketing: <Megaphone {...iconProps} />,
    education: <GraduationCap {...iconProps} />,
    settings: <SettingsIcon {...iconProps} />,
    forum: <MessagesSquare {...iconProps} />,
    fittingsystem: <Wrench {...iconProps} />,
    license: <CreditCard {...iconProps} />,
    payments: <DollarSign {...iconProps} />,
    certificate: <Award {...iconProps} />,
    brandmarketing: <Megaphone {...iconProps} />,
    brandguidelines: <BookOpen {...iconProps} />
  };
  return <span className="copilot-icon">{icons[type] || <Home {...iconProps} />}</span>;
}

// The HG monogram mark — replaces the robot mascot everywhere (audit #10).
function HGMark({ size = 34 }) {
  return (
    <span className="hg-mark" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      HG
    </span>
  );
}

/**
 * HOISTED TO MODULE SCOPE — Stage 1 of the App.js refactor (2026-08-13).
 *
 * This was declared INSIDE the App function body. React compares element types by reference,
 * so a function redefined on every App render is a new type every render, and React responded
 * by UNMOUNTING and remounting this component rather than re-rendering it — on every one of
 * App's 46 state values changing. Measured signature: mounts(sidebar) >= 1 on transitions the
 * Sidebar is never navigated to, where the correct number is 0.
 *
 * The body below is moved VERBATIM; only the indentation changed and the closed-over values
 * became props. Behaviour is intended to be identical.
 */
function Sidebar({ user, view, setView, copilotView, setCopilotView, sidebarOpen,
                   totalProgress, hgCertification, educationStatus, completedModules,
                   allLegacyModulesComplete, currentModule, openModule, isModuleLocked,
                   isModuleCompleted, closeSidebarOnMobile, handleLogout }) {
  return (
    <nav
      className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}
      data-testid="sidebar"
      onClick={(e) => {
        const it = e.target.closest('.nav-item');
        if (it && !it.classList.contains('locked')) closeSidebarOnMobile();
      }}
    >
      <div className="sidebar-logo">
        <img src="/images/hg-logo.png" alt="Henry-Griffitts" className="logo-image" />
        <p>Certified Fitter Hub</p>
      </div>
      <div className="sidebar-user">
        <div className="user-avatar">
          {user.picture ? (
            <img src={user.picture} alt={user.name} />
          ) : (
            <span>{user.name?.charAt(0)?.toUpperCase()}</span>
          )}
        </div>
        <div className="user-info">
          <div className="user-name">{user.name}</div>
          <div className="user-email">{user.email}</div>
        </div>
      </div>
      <div className="sidebar-progress">
        <div className="progress-label">
          <span>Course Progress</span>
          <span className="progress-percent">{totalProgress}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${totalProgress}%` }} />
        </div>
      </div>
      <div className="sidebar-nav">
        {/* EDUCATION-ONLY ACCOUNTS SEE THE COURSE AND NOTHING ELSE.
            THIS IS COSMETIC AND IS NEVER THE GATE. The server refuses these accounts on every
            non-education route via the default-deny middleware (education_access.py), and that
            is what protects the customer list. Deleting this block would make the app untidy;
            it would not make anything reachable. Rendering nav from a role is a courtesy to the
            student, not access control — the moment anyone treats it as the latter, the
            enumerated middleware audit is the thing that still holds. */}
        {user?.role === "education_only" ? (
          <>
            <div className="nav-section-header">Education</div>
            <div
              className={`nav-item ${view === "hg-education" ? "active" : ""}`}
              onClick={() => setView("hg-education")}
              data-testid="nav-hg-education"
            >
              <div className="nav-number"><CoPilotIcon type="education" /></div>
              <span className="nav-title">HG Education</span>
            </div>
            {hgCertification?.issued && (
              <div
                className={`nav-item ${view === "certificate" ? "active" : ""}`}
                onClick={() => setView("certificate")}
                data-testid="nav-certificate"
              >
                <div className="nav-number"><CoPilotIcon type="certificate" /></div>
                <span className="nav-title">Certificate</span>
              </div>
            )}
            <div
              className={`nav-item ${view === "settings" ? "active" : ""}`}
              onClick={() => setView("settings")}
              data-testid="nav-settings"
            >
              <div className="nav-number"><CoPilotIcon type="settings" /></div>
              <span className="nav-title">Settings</span>
            </div>
          </>
        ) : (
        <>
        {/* CoFitter Navigation */}
        <div className="nav-section-header">CoFitter</div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "dashboard" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("dashboard"); }}
          data-testid="nav-copilot-dashboard"
        >
          <div className="nav-number"><CoPilotIcon type="dashboard" /></div>
          <span className="nav-title">Dashboard</span>
        </div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "randy" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("randy"); }}
          data-testid="nav-copilot-randy"
        >
          <div className="nav-number"><CoPilotIcon type="randy" /></div>
          <span className="nav-title">CoFitter AI</span>
        </div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "crm" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("crm"); }}
          data-testid="nav-copilot-crm"
        >
          <div className="nav-number"><CoPilotIcon type="crm" /></div>
          <span className="nav-title">Customers</span>
        </div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "fitting" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("fitting"); }}
          data-testid="nav-copilot-fitting"
        >
          <div className="nav-number"><CoPilotIcon type="fitting" /></div>
          <span className="nav-title">Fittings</span>
        </div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "events" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("events"); }}
          data-testid="nav-copilot-events"
        >
          <div className="nav-number"><CoPilotIcon type="events" /></div>
          <span className="nav-title">Events</span>
        </div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "territory" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("territory"); }}
          data-testid="nav-copilot-territory"
        >
          <div className="nav-number"><CoPilotIcon type="territory" /></div>
          <span className="nav-title">Territory</span>
        </div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "marketing" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("marketing"); }}
          data-testid="nav-copilot-marketing"
        >
          <div className="nav-number"><CoPilotIcon type="marketing" /></div>
          <span className="nav-title">Marketing</span>
        </div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "brandmarketing" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("brandmarketing"); }}
          data-testid="nav-copilot-brandmarketing"
        >
          <div className="nav-number"><CoPilotIcon type="brandmarketing" /></div>
          <span className="nav-title">Brand &amp; Marketing</span>
        </div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "brandguidelines" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("brandguidelines"); }}
          data-testid="nav-copilot-brandguidelines"
        >
          <div className="nav-number"><CoPilotIcon type="brandguidelines" /></div>
          <span className="nav-title">Brand Guidelines</span>
        </div>
        
        <div className="nav-divider" />
        <div className="nav-section-header">Community</div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "forum" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("forum"); }}
          data-testid="nav-copilot-forum"
        >
          <div className="nav-number"><CoPilotIcon type="forum" /></div>
          <span className="nav-title">Round Table</span>
        </div>
        
        <div className="nav-divider" />
        <div className="nav-section-header">Account</div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "fittingsystem" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("fittingsystem"); }}
          data-testid="nav-copilot-fittingsystem"
        >
          <div className="nav-number"><CoPilotIcon type="fittingsystem" /></div>
          <span className="nav-title">My Equipment</span>
        </div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "license" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("license"); }}
          data-testid="nav-copilot-license"
        >
          <div className="nav-number"><CoPilotIcon type="license" /></div>
          <span className="nav-title">License</span>
        </div>
        <div
          className={`nav-item copilot-nav ${view === "copilot" && copilotView === "payments" ? "active" : ""}`}
          onClick={() => { setView("copilot"); setCopilotView("payments"); }}
          data-testid="nav-copilot-payments"
        >
          <div className="nav-number"><CoPilotIcon type="payments" /></div>
          <span className="nav-title">Payments</span>
        </div>
        
        <div className="nav-divider" />
        <div className="nav-section-header">Education</div>
        {/* P2 (2026-08-11, Randall's own observation): two education areas in one sidebar is
            confusing, and the legacy 0/36 badge made the real course look unstarted. The
            36-module skeleton is now ADMIN-ONLY — hidden from fitters, still reachable by
            anyone who needs the old view — rather than deleted, since nothing has migrated off
            it yet. Labelled legacy so it cannot be mistaken for the course again. */}
        {user?.is_admin && !LEGACY_MODULES_VOIDED && (
          <div
            className={`nav-item ${view === "education" ? "active" : ""}`}
            onClick={() => setView("education")}
            data-testid="nav-education"
          >
            <div className="nav-number"><CoPilotIcon type="education" /></div>
            <span className="nav-title">Training Modules (legacy)</span>
          </div>
        )}
        {/* HG Education is THE course; the skeleton above is admin-only. */}
        <div
          className={`nav-item ${view === "hg-education" ? "active" : ""}`}
          onClick={() => setView("hg-education")}
          data-testid="nav-hg-education"
        >
          <div className="nav-number"><CoPilotIcon type="education" /></div>
          <span className="nav-title">HG Education</span>
        </div>
        {/* PHASE 4 (2026-08-11): THE HINGE. This used to be `allComplete`, i.e. all 36 LEGACY
            modules — a curriculum being retired that nobody completes — so the Certificate item
            never appeared for any account. It now derives from HG Education Track A: every
            content chapter complete AND a passed, non-voided final exam. Issuance still
            requires admin approval; this only decides whether the item is VISIBLE. */}
        {hgCertification?.eligible && (
          <div
            className={`nav-item ${view === "certificate" ? "active" : ""}`}
            onClick={() => setView("certificate")}
            data-testid="nav-certificate"
          >
            <div className="nav-number"><CoPilotIcon type="certificate" /></div>
            <span className="nav-title">Certificate</span>
          </div>
        )}
        {user.is_admin && (
          <div
            className={`nav-item ${view === "admin" ? "active" : ""}`}
            onClick={() => setView("admin")}
            data-testid="nav-admin"
          >
            <div className="nav-number admin">A</div>
            <span className="nav-title">Admin</span>
          </div>
        )}
        <div
          className="nav-item"
          onClick={() => setView("settings")}
          data-testid="nav-settings"
        >
          <div className="nav-number">S</div>
          <span className="nav-title">Settings</span>
        </div>
        {/* P2 completion (2026-08-11, second walk-through): the legacy 36-module list itself
            was still rendering down the sidebar even after its nav entry was made admin-only,
            so a fitter saw the RETIRED curriculum's 36 modules listed beneath the real course.
            ADMIN-ONLY now, matching its nav entry — hidden, not deleted, because nothing has
            migrated off the legacy system yet. */}
        {/* VOID GUARD ADDED 2026-09-10. This list was made admin-only on 2026-08-11,
            eight days before LEGACY_MODULES_VOIDED existed, so it never picked the
            constant up. It was the last live caller of openModule(), which set
            view="module" for a view that then refused to render -- an admin clicking a
            module got a blank page with a "Module N / Slides" breadcrumb and a slide
            counter. The header comment on LEGACY_MODULES_VOIDED claimed these views
            "can no longer be opened"; this is the path that could still open them. */}
        {user?.is_admin && !LEGACY_MODULES_VOIDED && <div className="nav-divider" />}
        {user?.is_admin && !LEGACY_MODULES_VOIDED && CATEGORIES.map((cat) => {
          const catModules = MODULES.filter((m) => m.category === cat);
          return (
            <div key={cat}>
              <div className="nav-category">
                <span className="cat-icon">{getCategoryIcon(cat)}</span> {cat}
              </div>
              {catModules.map((mod) => (
                <div
                  key={mod.id}
                  className={`nav-item ${isModuleLocked(mod.id) ? "locked" : ""} ${isModuleCompleted(mod.id) ? "completed" : ""} ${currentModule?.id === mod.id ? "active" : ""}`}
                  onClick={() => !isModuleLocked(mod.id) && openModule(mod)}
                  data-testid={`nav-module-${mod.id}`}
                >
                  <div className="nav-number">{isModuleLocked(mod.id) ? <Lock size={11} strokeWidth={1.8} /> : mod.id}</div>
                  <span className="nav-title">{mod.title}</span>
                  {isModuleCompleted(mod.id) && <span className="nav-check">✓</span>}
                </div>
              ))}
            </div>
          );
        })}
        </>
        )}
      </div>
    </nav>
  );
}


/**
 * HOISTED TO MODULE SCOPE — Stage 1. See the note on Sidebar above; same defect, same fix.
 */
function TopBar({ user, view, copilotView, sidebarOpen, setSidebarOpen, currentModule, currentSlide, completedModules }) {
  let breadcrumb = "Dashboard";
  let badge = null;
  // The breadcrumb drew "Module N / Slides" over an empty body while the view was
  // voided. Guarded 2026-09-10 so the shell cannot advertise a screen that will not
  // render, even if some future path sets this view again.
  if (view === "module" && currentModule && !LEGACY_MODULES_VOIDED) {
    breadcrumb = `Module ${currentModule.id} / Slides`;
    badge = <span className="badge badge-gold">Slide {currentSlide + 1} of {currentModule.slides.length}</span>;
  }
  if (view === "quiz") {
    breadcrumb = `Module ${currentModule.id} / Quiz`;
    badge = <span className="badge badge-gray">{currentModule.quiz.length} Questions</span>;
  }
  if (view === "admin") breadcrumb = "Admin Analytics";
  if (view === "certificate") breadcrumb = "Certificate";
  if (view === "settings") breadcrumb = "Settings";
  // AUDIT FIX #5: every CoFitter business page gets its real breadcrumb —
  // it was stuck on "Dashboard" for all of them.
  if (view === "copilot") {
    const copilotLabels = {
      dashboard: "Dashboard", randy: "CoFitter AI", crm: "Customers",
      fitting: "Fittings", events: "Events", territory: "Territory",
      marketing: "Marketing", forum: "Round Table",
      fittingsystem: "My Equipment", license: "License", payments: "Payments",
    };
    breadcrumb = copilotLabels[copilotView] || "Dashboard";
  }

  return (
    <div className="topbar" data-testid="topbar">
      <button className="menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        <Menu size={18} />
      </button>
      <div className="topbar-breadcrumb">
        Fitter Hub &nbsp;/&nbsp; <span>{breadcrumb}</span>
      </div>
      <div className="topbar-actions">
        {badge}
        {/* P2 completion (2026-08-11, second walk-through): the worse of the two legacy
            leftovers. This badge counted progress against the curriculum being RETIRED, in the
            top bar, next to the real one — so a fitter who had genuinely started HG Education
            read "0/36 Complete" and concluded the course had not begun. ADMIN-ONLY until the
            legacy system is formally decommissioned. The contextual `badge` above is unrelated
            and stays for everyone. */}
        {/* AND GATED ON THE VOID FLAG TOO (2026-09-01). Admin-only was not enough: Randall IS an
            admin, so walking his own course he read "0/36 COMPLETE" in the top bar — a count
            against the retired curriculum, sitting where the real one should be. The legacy NAV
            item beside it already respected LEGACY_MODULES_VOIDED; this badge never did, so the
            two disagreed about whether the old system still existed. */}
        {user?.is_admin && !LEGACY_MODULES_VOIDED && (
          <span className="badge badge-green" data-testid="legacy-module-progress-badge">
            {completedModules.length}/{MODULES.length} Complete
          </span>
        )}
      </div>
    </div>
  );
}

// HOISTED — Stage 2. Tier 1: measured to lose typed data when remounted.
function CRMView({ view, golfers, fittings, setSelectedGolfer }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [localGolfers, setLocalGolfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGolfer, setNewGolfer] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    handicap: "", typical_miss: "", struggle_club: "", seven_iron_distance: "", source: ""
  });

  async function loadLocalGolfers(search = "") {
    setLoading(true);
    try {
      const url = search 
        ? `${API_URL}/api/cofitter/golfers?search=${encodeURIComponent(search)}`
        : `${API_URL}/api/cofitter/golfers`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLocalGolfers(data.golfers || []);
      }
    } catch (err) {
      console.error("Failed to load golfers:", err);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadLocalGolfers();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        loadLocalGolfers(searchTerm);
      } else {
        loadLocalGolfers();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  async function addGolfer() {
    if (!newGolfer.first_name || !newGolfer.last_name) return;
    
    try {
      const res = await fetch(`${API_URL}/api/cofitter/golfers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newGolfer,
          handicap: newGolfer.handicap ? parseFloat(newGolfer.handicap) : null,
          seven_iron_distance: newGolfer.seven_iron_distance ? parseInt(newGolfer.seven_iron_distance) : null
        })
      });
      
      if (res.ok) {
        setNewGolfer({ first_name: "", last_name: "", email: "", phone: "", handicap: "", typical_miss: "", struggle_club: "", seven_iron_distance: "", source: "" });
        setShowAddForm(false);
        loadLocalGolfers();
      }
    } catch (err) {
      console.error("Failed to add golfer:", err);
    }
  }

  return (
    <div className="copilot-content crm-view" data-testid="crm-view">
      <div className="copilot-header">
        <h2><CoPilotIcon type="crm" /> Customer Management</h2>
        <p>Manage your golfers and track relationships</p>
      </div>

      <div className="crm-toolbar">
        <input 
          type="text" 
          placeholder="Search golfers..." 
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="search-input"
          data-testid="crm-search"
        />
        <button className="btn btn-primary" onClick={() => setShowAddForm(true)} data-testid="add-golfer-btn">
          + Add Golfer
        </button>
      </div>

      {showAddForm && (
        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Add New Golfer</h3>
            <div className="form-grid">
              <input placeholder="First Name *" value={newGolfer.first_name} onChange={e => setNewGolfer({...newGolfer, first_name: e.target.value})} />
              <input placeholder="Last Name *" value={newGolfer.last_name} onChange={e => setNewGolfer({...newGolfer, last_name: e.target.value})} />
              <input placeholder="Email" value={newGolfer.email} onChange={e => setNewGolfer({...newGolfer, email: e.target.value})} />
              <input placeholder="Phone" value={newGolfer.phone} onChange={e => setNewGolfer({...newGolfer, phone: e.target.value})} />
              <input placeholder="Handicap" type="number" value={newGolfer.handicap} onChange={e => setNewGolfer({...newGolfer, handicap: e.target.value})} />
              <input placeholder="7-Iron Distance (yards)" type="number" value={newGolfer.seven_iron_distance} onChange={e => setNewGolfer({...newGolfer, seven_iron_distance: e.target.value})} />
              <select value={newGolfer.typical_miss} onChange={e => setNewGolfer({...newGolfer, typical_miss: e.target.value})}>
                <option value="">Typical Miss</option>
                <option value="slice">Slice (Right)</option>
                <option value="hook">Hook (Left)</option>
                <option value="straight">Straight</option>
                <option value="thin">Thin</option>
                <option value="fat">Fat</option>
              </select>
              <select value={newGolfer.source} onChange={e => setNewGolfer({...newGolfer, source: e.target.value})}>
                <option value="">Source</option>
                <option value="referral">Referral</option>
                <option value="event">Fitting Event</option>
                <option value="range">Driving Range</option>
                <option value="lesson">Lesson</option>
                <option value="walk-in">Walk-in</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addGolfer}>Add Golfer</button>
            </div>
          </div>
        </div>
      )}

      <div className="golfers-grid">
        {loading && <p>Loading...</p>}
        {!loading && localGolfers.map(golfer => (
          <div key={golfer.golfer_id} className="golfer-card" onClick={() => setSelectedGolfer(golfer)}>
            {/* GUARDED (2026-08-13). `golfer.first_name[0]` on a record without a first_name
                threw "Cannot read properties of undefined (reading '0')" and, with no error
                boundary anywhere, took the ENTIRE APP to a white screen — measured, #root
                innerHTML length 0. GolferUpdate allows first_name: Optional[str] = None, so
                the first real customer can produce such a record. The initials degrade; the
                application does not. */}
            <div className="golfer-avatar">{initialsOf(golfer)}</div>
            <div className="golfer-info">
              <h4>{golfer.first_name} {golfer.last_name}</h4>
              {golfer.handicap && <span className="golfer-handicap">HCP: {golfer.handicap}</span>}
              {golfer.email && <span className="golfer-email">{golfer.email}</span>}
            </div>
            <div className="golfer-meta">
              {golfer.fittings?.length > 0 && <span className="badge">{golfer.fittings.length} fittings</span>}
              {golfer.typical_miss && <span className="miss-badge">{golfer.typical_miss}</span>}
            </div>
          </div>
        ))}
        {!loading && localGolfers.length === 0 && (
          <div className="empty-state-hg" data-testid="empty-golfers">
            <Users size={28} strokeWidth={1.5} />
            <p>No golfers yet — your customer book starts with one name.</p>
            <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>+ Add your first customer</button>
          </div>
        )}
      </div>
    </div>
  );
}

// HOISTED — Stage 2. Tier 1: measured to lose typed data when remounted.
function FittingView({ view, golfers, fittings }) {
  const [showNewFitting, setShowNewFitting] = useState(false);
  const [localFittings, setLocalFittings] = useState([]);
  const [localGolfers, setLocalGolfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFitting, setNewFitting] = useState({
    golfer_id: "", fitting_type: "full", swing_speed: "", ball_flight: "", notes: ""
  });

  async function loadLocalFittings() {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cofitter/fittings`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLocalFittings(data.fittings || []);
      }
    } catch (err) {
      console.error("Failed to load fittings:", err);
    }
    setLoading(false);
  }

  async function loadGolfersForDropdown() {
    try {
      const res = await fetch(`${API_URL}/api/cofitter/golfers`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLocalGolfers(data.golfers || []);
      }
    } catch (err) {
      console.error("Failed to load golfers:", err);
    }
  }

  useEffect(() => {
    loadLocalFittings();
    loadGolfersForDropdown();
  }, []);

  async function createFitting() {
    if (!newFitting.golfer_id) return;
    
    try {
      const res = await fetch(`${API_URL}/api/cofitter/fittings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newFitting,
          swing_speed: newFitting.swing_speed ? parseInt(newFitting.swing_speed) : null
        })
      });
      
      if (res.ok) {
        setShowNewFitting(false);
        setNewFitting({ golfer_id: "", fitting_type: "full", swing_speed: "", ball_flight: "", notes: "" });
        loadLocalFittings();
      }
    } catch (err) {
      console.error("Failed to create fitting:", err);
    }
  }

  return (
    <div className="copilot-content fitting-view" data-testid="fitting-view">
      <div className="copilot-header">
        <h2><CoPilotIcon type="fitting" /> Fitting Workflow</h2>
        <p>Manage and track fitting sessions</p>
      </div>

      <div className="crm-toolbar">
        <button className="btn btn-primary" onClick={() => setShowNewFitting(true)} data-testid="new-fitting-btn">
          + New Fitting
        </button>
      </div>

      {showNewFitting && (
        <div className="modal-overlay" onClick={() => setShowNewFitting(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Start New Fitting</h3>
            <div className="form-grid">
              <select value={newFitting.golfer_id} onChange={e => setNewFitting({...newFitting, golfer_id: e.target.value})}>
                <option value="">Select Golfer *</option>
                {localGolfers.map(g => (
                  <option key={g.golfer_id} value={g.golfer_id}>{g.first_name} {g.last_name}</option>
                ))}
              </select>
              <select value={newFitting.fitting_type} onChange={e => setNewFitting({...newFitting, fitting_type: e.target.value})}>
                <option value="full">Full Bag Fitting</option>
                <option value="iron">Irons Only</option>
                <option value="driver">Driver/Woods</option>
                <option value="wedge">Wedges</option>
                <option value="putter">Putter</option>
              </select>
              <input placeholder="Swing Speed (mph)" type="number" value={newFitting.swing_speed} onChange={e => setNewFitting({...newFitting, swing_speed: e.target.value})} />
              <select value={newFitting.ball_flight} onChange={e => setNewFitting({...newFitting, ball_flight: e.target.value})}>
                <option value="">Ball Flight</option>
                <option value="high">High</option>
                <option value="mid">Mid</option>
                <option value="low">Low</option>
              </select>
            </div>
            <textarea placeholder="Notes..." value={newFitting.notes} onChange={e => setNewFitting({...newFitting, notes: e.target.value})} />
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowNewFitting(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createFitting}>Start Fitting</button>
            </div>
          </div>
        </div>
      )}

      <div className="fittings-list">
        {loading && <p>Loading...</p>}
        {!loading && localFittings.map(fitting => (
          <div key={fitting.fitting_id} className={`fitting-card status-${fitting.status}`}>
            <div className="fitting-header">
              {/* GUARDED (2026-08-13) — same defect as the golfer avatar above: an absent
                  fitting_type threw on .toUpperCase() and white-screened the whole app. */}
              <span className="fitting-type">
                {String(fitting.fitting_type || "—").toUpperCase()}
              </span>
              <span className={`fitting-status ${fitting.status}`}>{fitting.status}</span>
            </div>
            <div className="fitting-details">
              <p><strong>Golfer:</strong> {fitting.golfer_id}</p>
              {fitting.swing_speed && <p><strong>Swing Speed:</strong> {fitting.swing_speed} mph</p>}
              {fitting.ball_flight && <p><strong>Ball Flight:</strong> {fitting.ball_flight}</p>}
              <p className="fitting-date">{new Date(fitting.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        ))}
        {!loading && localFittings.length === 0 && (
          <div className="empty-state-hg" data-testid="empty-fittings">
            <Target size={28} strokeWidth={1.5} />
            <p>No fittings yet — the first one takes about 40 minutes.</p>
            <button className="btn btn-primary" onClick={() => setShowNewFitting(true)}>+ Start a fitting</button>
          </div>
        )}
      </div>
    </div>
  );
}

// HOISTED — Stage 2. Tier 1: measured to lose typed data when remounted.
function EventsView({ view, events }) {
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [localEvents, setLocalEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEvent, setNewEvent] = useState({
    name: "", facility_name: "", event_date: "", start_time: "09:00", end_time: "17:00", slot_duration: 60
  });

  async function loadLocalEvents() {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cofitter/events`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLocalEvents(data.events || []);
      }
    } catch (err) {
      console.error("Failed to load events:", err);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadLocalEvents();
  }, []);

  async function createEvent() {
    if (!newEvent.name || !newEvent.facility_name || !newEvent.event_date) return;
    
    try {
      const res = await fetch(`${API_URL}/api/cofitter/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newEvent)
      });
      
      if (res.ok) {
        setShowNewEvent(false);
        setNewEvent({ name: "", facility_name: "", event_date: "", start_time: "09:00", end_time: "17:00", slot_duration: 60 });
        loadLocalEvents();
      }
    } catch (err) {
      console.error("Failed to create event:", err);
    }
  }

  return (
    <div className="copilot-content events-view" data-testid="events-view">
      <div className="copilot-header">
        <h2><CoPilotIcon type="events" /> Fitting Day Events</h2>
        <p>Schedule and manage fitting events at facilities</p>
      </div>

      <div className="crm-toolbar">
        <button className="btn btn-primary" onClick={() => setShowNewEvent(true)} data-testid="new-event-btn">
          + New Event
        </button>
      </div>

      {showNewEvent && (
        <div className="modal-overlay" onClick={() => setShowNewEvent(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Create Fitting Day Event</h3>
            <div className="form-grid">
              <input placeholder="Event Name *" value={newEvent.name} onChange={e => setNewEvent({...newEvent, name: e.target.value})} />
              <input placeholder="Facility Name *" value={newEvent.facility_name} onChange={e => setNewEvent({...newEvent, facility_name: e.target.value})} />
              <input type="date" value={newEvent.event_date} onChange={e => setNewEvent({...newEvent, event_date: e.target.value})} />
              <input type="time" value={newEvent.start_time} onChange={e => setNewEvent({...newEvent, start_time: e.target.value})} />
              <input type="time" value={newEvent.end_time} onChange={e => setNewEvent({...newEvent, end_time: e.target.value})} />
              <select value={newEvent.slot_duration} onChange={e => setNewEvent({...newEvent, slot_duration: parseInt(e.target.value)})}>
                <option value={45}>45 min slots</option>
                <option value={60}>60 min slots</option>
                <option value={90}>90 min slots</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowNewEvent(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createEvent}>Create Event</button>
            </div>
          </div>
        </div>
      )}

      <div className="events-grid">
        {loading && <p>Loading...</p>}
        {!loading && localEvents.map(event => (
          <div key={event.event_id} className={`event-card status-${event.status}`}>
            <div className="event-date-badge">
              <span className="event-month">{new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' })}</span>
              <span className="event-day">{new Date(event.event_date).getDate()}</span>
            </div>
            <div className="event-info">
              <h4>{event.name}</h4>
              <p className="event-facility">{event.facility_name}</p>
              <p className="event-time">{event.start_time} - {event.end_time}</p>
              <div className="event-slots">
                <span className="slots-booked">{event.slots?.filter(s => s.status === "booked").length || 0}</span>
                <span className="slots-separator">/</span>
                <span className="slots-total">{event.slots?.length || 0} slots</span>
              </div>
            </div>
            <span className={`event-status-badge ${event.status}`}>{event.status}</span>
          </div>
        ))}
        {!loading && localEvents.length === 0 && (
          <div className="empty-state-hg" data-testid="empty-events">
            <Calendar size={28} strokeWidth={1.5} />
            <p>No events scheduled — a fitting day fills a book fast.</p>
            <button className="btn btn-primary" onClick={() => setShowNewEvent(true)}>+ Create a fitting day</button>
          </div>
        )}
      </div>
    </div>
  );
}

// HOISTED — Stage 2. Tier 1: measured to lose typed data when remounted.
function TerritoryView({ view, territory }) {
  const [showAddFacility, setShowAddFacility] = useState(false);
  const [localTerritory, setLocalTerritory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFacility, setNewFacility] = useState({
    name: "", address: "", contact_name: "", contact_email: "", contact_phone: "", outreach_status: "not_contacted"
  });

  async function loadLocalTerritory() {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cofitter/territory`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLocalTerritory(data.facilities || []);
      }
    } catch (err) {
      console.error("Failed to load territory:", err);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadLocalTerritory();
  }, []);

  async function addFacility() {
    if (!newFacility.name) return;
    
    try {
      const res = await fetch(`${API_URL}/api/cofitter/territory`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newFacility)
      });
      
      if (res.ok) {
        setShowAddFacility(false);
        setNewFacility({ name: "", address: "", contact_name: "", contact_email: "", contact_phone: "", outreach_status: "not_contacted" });
        loadLocalTerritory();
      }
    } catch (err) {
      console.error("Failed to add facility:", err);
    }
  }

  const statusColors = {
    not_contacted: "#666",
    contacted: "#f5a623",
    interested: "#48B64C",
    scheduled: "#2196F3",
    partner: "#48B64C"
  };

  return (
    <div className="copilot-content territory-view" data-testid="territory-view">
      <div className="copilot-header">
        <h2><CoPilotIcon type="territory" /> Territory Development</h2>
        <p>Track facilities and build partnerships</p>
      </div>

      <div className="crm-toolbar">
        <button className="btn btn-primary" onClick={() => setShowAddFacility(true)} data-testid="add-facility-btn">
          + Add Facility
        </button>
      </div>

      {showAddFacility && (
        <div className="modal-overlay" onClick={() => setShowAddFacility(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Add Facility to Territory</h3>
            <div className="form-grid">
              <input placeholder="Facility Name *" value={newFacility.name} onChange={e => setNewFacility({...newFacility, name: e.target.value})} />
              <input placeholder="Address" value={newFacility.address} onChange={e => setNewFacility({...newFacility, address: e.target.value})} />
              <input placeholder="Contact Name" value={newFacility.contact_name} onChange={e => setNewFacility({...newFacility, contact_name: e.target.value})} />
              <input placeholder="Contact Email" value={newFacility.contact_email} onChange={e => setNewFacility({...newFacility, contact_email: e.target.value})} />
              <input placeholder="Contact Phone" value={newFacility.contact_phone} onChange={e => setNewFacility({...newFacility, contact_phone: e.target.value})} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowAddFacility(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addFacility}>Add Facility</button>
            </div>
          </div>
        </div>
      )}

      <div className="territory-grid">
        {loading && <p>Loading...</p>}
        {!loading && localTerritory.map(facility => (
          <div key={facility.facility_id} className="facility-card">
            <div className="facility-status-indicator" style={{ backgroundColor: statusColors[facility.outreach_status] }} />
            <div className="facility-info">
              <h4>{facility.name}</h4>
              {facility.address && <p className="facility-address">{facility.address}</p>}
              {facility.contact_name && <p className="facility-contact">{facility.contact_name}</p>}
            </div>
            <span className={`outreach-status status-${facility.outreach_status}`}>
              {facility.outreach_status.replace('_', ' ')}
            </span>
          </div>
        ))}
        {!loading && localTerritory.length === 0 && (
          <div className="empty-state">
            <p>No facilities in your territory yet. Add golf courses and ranges!</p>
          </div>
        )}
      </div>
    </div>
  );
}

// HOISTED — Stage 2. Tier 1: measured to lose typed data when remounted.
function AnnouncementsTab({ announcements, loadAnnouncements }) {
  const [annTitle, setAnnTitle] = useState("");
  const [annContent, setAnnContent] = useState("");
  const [annPriority, setAnnPriority] = useState("normal");
  const [creating, setCreating] = useState(false);
  const [localAnnouncements, setLocalAnnouncements] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loaded) {
      loadLocalAnnouncements();
    }
  }, [loaded]);

  async function loadLocalAnnouncements() {
    try {
      const res = await fetch(`${API_URL}/api/announcements`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLocalAnnouncements(data.announcements || []);
      }
    } catch (err) {
      console.error("Failed to load announcements:", err);
    }
    setLoaded(true);
  }

  async function createAnnouncement() {
    if (!annTitle.trim() || !annContent.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/announcements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title: annTitle, content: annContent, priority: annPriority })
      });
      if (res.ok) {
        setAnnTitle("");
        setAnnContent("");
        setAnnPriority("normal");
        loadLocalAnnouncements();
        loadAnnouncements(); // Refresh global state
      }
    } catch (err) {
      alert("Failed to create announcement");
    }
    setCreating(false);
  }

  async function deleteAnnouncement(id) {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await fetch(`${API_URL}/api/announcements/${id}`, { method: "DELETE", credentials: "include" });
      loadLocalAnnouncements();
      loadAnnouncements();
    } catch (err) {
      alert("Failed to delete announcement");
    }
  }

  return (
    <>
      <div className="section-title">Course Announcements</div>
      <p className="section-sub">Create announcements that appear on student dashboards</p>
      
      <div className="admin-card">
        <h3>Create New Announcement</h3>
        <div className="announcement-form">
          <input 
            type="text" 
            value={annTitle} 
            onChange={e => setAnnTitle(e.target.value)} 
            placeholder="Announcement title" 
            className="bulk-email-input"
            data-testid="announcement-title-input"
          />
          <textarea 
            value={annContent} 
            onChange={e => setAnnContent(e.target.value)} 
            placeholder="Announcement message" 
            className="bulk-email-textarea" 
            rows={3}
            data-testid="announcement-content-input"
          />
          <div className="announcement-options">
            <label>Priority:</label>
            <select value={annPriority} onChange={e => setAnnPriority(e.target.value)} className="bulk-email-select">
              <option value="normal">Normal</option>
              <option value="important">Important</option>
              <option value="urgent">Urgent</option>
            </select>
            <button 
              className="btn btn-primary" 
              onClick={createAnnouncement} 
              disabled={creating || !annTitle.trim() || !annContent.trim()}
              data-testid="create-announcement-btn"
            >
              {creating ? "Creating..." : "Post Announcement"}
            </button>
          </div>
        </div>
      </div>

      <div className="admin-card">
        <h3>Active Announcements ({localAnnouncements.length})</h3>
        <div className="announcements-list">
          {localAnnouncements.map(ann => (
            <div key={ann.announcement_id} className={`announcement-admin-item priority-${ann.priority}`}>
              <div className="announcement-admin-header">
                <span className={`priority-badge ${ann.priority}`}>{ann.priority}</span>
                <span className="announcement-date">{new Date(ann.created_at).toLocaleDateString()}</span>
              </div>
              <h4>{ann.title}</h4>
              <p>{ann.content}</p>
              <button className="btn btn-danger btn-sm" onClick={() => deleteAnnouncement(ann.announcement_id)}>Delete</button>
            </div>
          ))}
          {localAnnouncements.length === 0 && (
            <p className="no-announcements">No announcements yet. Create one above.</p>
          )}
        </div>
      </div>
    </>
  );
}

/** Which admin tab opens when Admin is clicked. "pilot" WHILE THE PILOT IS RUNNING — put
 *  back to "overview" on the same day the tab itself is removed, so the app never opens on
 *  a tab that no longer exists. */
export const ADMIN_DEFAULT_TAB = "pilot";

/** A stored ISO date as a person reads it. Empty renders as an em dash, not as "Invalid
 *  Date" — an account with no last_login has never signed in, which is a fact worth showing
 *  rather than an error worth printing. */
function shortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// HOISTED — Stage 2. Tier 1: measured to lose typed data when remounted.
function AdminAnalytics({ user, adminData, adminLoading, loadAdminData, loadUserDetails,
                        selectedUser, setSelectedUser, groups,
                        announcements, loadAnnouncements,
                        activeTab, setActiveTab }) {
  // The tab lives in App so anything else can send an admin straight to it — the dashboard
  // card does exactly that. Which tab opens first is ADMIN_DEFAULT_TAB, one constant, below.
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [campaignRefresh, setCampaignRefresh] = useState(0);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailFilter, setEmailFilter] = useState("all");
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDesc, setNewGroupDesc] = useState("");
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [adminEmails, setAdminEmails] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (activeTab === "settings") {
      loadAdminSettings();
    }
  }, [activeTab]);

  async function loadAdminSettings() {
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAdminEmails(data.admin_emails || []);
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  }

  if (!user?.is_admin) {
    return <div className="content">Access denied</div>;
  }

  if (adminLoading && !adminData) {
    return (
      <div className="content">
        <div className="section-title">Admin Analytics</div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  async function exportCSV() {
    try {
      const res = await fetch(`${API_URL}/api/admin/export/csv`, { credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hg-training-export-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (err) {
      alert("Failed to export data");
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/import-users`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      if (res.ok) {
        const result = await res.json();
        setImportResult(result);
        loadAdminData(); // Refresh data
      } else {
        setImportResult({ error: "Import failed" });
      }
    } catch (err) {
      setImportResult({ error: "Import failed: " + err.message });
    }
    setImporting(false);
    e.target.value = '';
  }

  async function createGroup() {
    if (!newGroupName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newGroupName, description: newGroupDesc })
      });
      if (res.ok) {
        setNewGroupName("");
        setNewGroupDesc("");
        loadAdminData();
      }
    } catch (err) {
      alert("Failed to create group");
    }
  }

  async function deleteGroup(groupId) {
    if (!window.confirm("Delete this group? Users will be unassigned.")) return;
    try {
      await fetch(`${API_URL}/api/groups/${groupId}`, {
        method: "DELETE",
        credentials: "include"
      });
      loadAdminData();
    } catch (err) {
      alert("Failed to delete group");
    }
  }

  async function addAdmin() {
    if (!newAdminEmail.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/add-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: newAdminEmail })
      });
      if (res.ok) {
        const data = await res.json();
        setAdminEmails(data.admin_emails);
        setNewAdminEmail("");
      }
    } catch (err) {
      alert("Failed to add admin");
    }
  }

  async function removeAdmin(email) {
    if (!window.confirm(`Remove ${email} as admin?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/remove-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        const data = await res.json();
        setAdminEmails(data.admin_emails);
      }
    } catch (err) {
      alert("Failed to remove admin");
    }
  }

  // PREPARES, DOES NOT SEND (Randall, 2026-11-02). Both buttons make a campaign, and it waits in MailCampaigns below
  // until it has been read and approved.
  async function sendBulkEmail() {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      alert("Please enter subject and message");
      return;
    }
    setSendingBulk(true);
    setBulkResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/send-bulk-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ subject: emailSubject, message: emailMessage, filter_type: emailFilter })
      });
      const result = await res.json().catch(() => ({}));
      if (res.ok) {
        setBulkResult(result);
        setCampaignRefresh((n) => n + 1);
        setEmailSubject("");
        setEmailMessage("");
      } else {
        alert(result.detail || "Could not prepare the email");
      }
    } catch (err) {
      alert("Could not prepare the email");
    }
    setSendingBulk(false);
  }

  async function sendReminders() {
    setSendingReminders(true);
    setBulkResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/send-reminders`, {
        method: "POST",
        credentials: "include"
      });
      const result = await res.json().catch(() => ({}));
      if (res.ok) {
        setBulkResult(result);
        setCampaignRefresh((n) => n + 1);
      } else {
        alert(result.detail || "Could not prepare the reminders");
      }
    } catch (err) {
      alert("Could not prepare the reminders");
    }
    setSendingReminders(false);
  }

  // Announcements Tab Component

  // Progress Importer Component

  // Videos Tab Component
  function VideosTab() {
    const [videoStatus, setVideoStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
      loadVideoStatus();
    }, []);

    async function loadVideoStatus() {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/video-generation-status`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setVideoStatus(data);
        }
      } catch (err) {
        console.error("Failed to load video status:", err);
      }
      setLoading(false);
    }

    async function generateAllVideos() {
      if (!window.confirm("This will start generating AI videos for all 36 modules. This process takes time. Continue?")) return;
      setGenerating(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/generate-all-videos`, {
          method: "POST",
          credentials: "include"
        });
        if (res.ok) {
          const result = await res.json();
          alert(result.message);
          loadVideoStatus();
        }
      } catch (err) {
        alert("Failed to start video generation");
      }
      setGenerating(false);
    }

    async function generateSingleVideo(moduleId, title, category) {
      try {
        const res = await fetch(`${API_URL}/api/admin/generate-module-video`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ module_id: moduleId, module_title: title, category: category })
        });
        if (res.ok) {
          loadVideoStatus();
        }
      } catch (err) {
        alert("Failed to start video generation");
      }
    }

    async function deleteVideo(moduleId) {
      if (!window.confirm("Delete this video?")) return;
      try {
        await fetch(`${API_URL}/api/admin/module-videos/${moduleId}`, { method: "DELETE", credentials: "include" });
        loadVideoStatus();
      } catch (err) {
        alert("Failed to delete video");
      }
    }

    const statusCounts = videoStatus?.status_counts || { pending: 0, generating: 0, completed: 0, failed: 0 };

    return (
      <>
        <div className="section-title">AI Video Generation</div>
        <p className="section-sub">Generate AI-powered educational videos for each module using Sora 2</p>
        
        <div className="stats-grid video-stats">
          <div className="stat-card">
            <div className="stat-value">{statusCounts.completed}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statusCounts.generating}</div>
            <div className="stat-label">Generating</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statusCounts.pending}</div>
            <div className="stat-label">Pending</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statusCounts.failed}</div>
            <div className="stat-label">Failed</div>
          </div>
        </div>

        <div className="admin-card">
          <h3>Batch Generation</h3>
          <p className="settings-info">Generate AI videos for all modules with varied styles and durations</p>
          <div className="video-actions">
            <button 
              className="btn btn-primary" 
              onClick={generateAllVideos} 
              disabled={generating}
              data-testid="generate-all-videos-btn"
            >
              {generating ? "Starting..." : "Generate All Module Videos"}
            </button>
            <button className="btn btn-secondary" onClick={loadVideoStatus} disabled={loading}>
              {loading ? "Loading..." : "Refresh Status"}
            </button>
          </div>
        </div>

        <div className="admin-card">
          <h3>Module Videos</h3>
          <div className="video-list">
            {MODULES.map(mod => {
              const video = videoStatus?.videos?.find(v => v.module_id === mod.id);
              const status = video?.status || "not_started";
              
              return (
                <div key={mod.id} className={`video-item status-${status}`}>
                  <div className="video-item-info">
                    <span className="video-module-num">Module {mod.id}</span>
                    <span className="video-module-title">{mod.title}</span>
                  </div>
                  <div className="video-item-status">
                    {status === "completed" && (
                      <>
                        <span className="status-badge completed">Completed</span>
                        <a href={video.video_url} target="_blank" rel="noopener noreferrer" className="btn btn-text btn-sm">View</a>
                        <button className="btn btn-text btn-danger btn-sm" onClick={() => deleteVideo(mod.id)}>Delete</button>
                      </>
                    )}
                    {status === "generating" && <span className="status-badge generating">Generating...</span>}
                    {status === "pending" && <span className="status-badge pending">Queued</span>}
                    {status === "failed" && (
                      <>
                        <span className="status-badge failed">Failed</span>
                        <button className="btn btn-text btn-sm" onClick={() => generateSingleVideo(mod.id, mod.title, mod.category)}>Retry</button>
                      </>
                    )}
                    {status === "not_started" && (
                      <button className="btn btn-text btn-sm" onClick={() => generateSingleVideo(mod.id, mod.title, mod.category)}>Generate</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="content admin-content" data-testid="admin-analytics">
      <div className="admin-tabs">
        {/* FIRST, AND THE ONE THAT OPENS. The tracker was correct and unreachable: eighth of
            nine tabs, with nothing anywhere else in the app pointing at it, so knowing who
            had opted in meant remembering it existed and then hunting for it.

            The pilot is still a fixed-term cohort rather than a permanent part of the admin
            surface. When it ends, this button goes and ADMIN_DEFAULT_TAB goes back to
            "overview" — two lines, both marked. */}
        <button className={`admin-tab ${activeTab === "pilot" ? "active" : ""}`} onClick={() => setActiveTab("pilot")} data-testid="admin-tab-pilot">Pilot Invites &amp; Opt-ins</button>
        {/* WHERE EVERY ENROLLED FITTER STANDS IN THE COURSE, and who needs attention (components/TrainingStatus). */}
        <button className={`admin-tab ${activeTab === "training" ? "active" : ""}`} onClick={() => setActiveTab("training")} data-testid="admin-tab-training">Training Status</button>
        <button className={`admin-tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>Overview</button>
        <button className={`admin-tab ${activeTab === "users" ? "active" : ""}`} onClick={() => setActiveTab("users")}>Users</button>
        <button className={`admin-tab ${activeTab === "groups" ? "active" : ""}`} onClick={() => setActiveTab("groups")}>Groups</button>
        <button className={`admin-tab ${activeTab === "import" ? "active" : ""}`} onClick={() => setActiveTab("import")}>Import</button>
        <button className={`admin-tab ${activeTab === "email" ? "active" : ""}`} onClick={() => setActiveTab("email")}>Email</button>
        <button className={`admin-tab ${activeTab === "announcements" ? "active" : ""}`} onClick={() => setActiveTab("announcements")}>Announcements</button>
        <button className={`admin-tab ${activeTab === "videos" ? "active" : ""}`} onClick={() => setActiveTab("videos")}>AI Videos</button>
        <button className={`admin-tab ${activeTab === "pga" ? "active" : ""}`} onClick={() => setActiveTab("pga")} data-testid="admin-tab-pga">PGA / LPGA</button>
        <button className={`admin-tab ${activeTab === "settings" ? "active" : ""}`} onClick={() => setActiveTab("settings")}>Settings</button>
      </div>

      {/* PGA / LPGA COMPLETION SUBMISSION (Randall, 2026-09-22). Reads and renders; there is
          no control here that sends anything, because no route and no module can. */}
      {activeTab === "pga" && <PgaSubmissionPanel />}

      {activeTab === "training" && <TrainingStatus />}

      {activeTab === "overview" && adminData && (
        <>
          <div className="admin-header">
            <div>
              <div className="section-title">Dashboard Overview</div>
              <p className="section-sub">{adminData.total_users} registered accounts</p>
            </div>
            <div className="admin-actions">
              <button className="btn btn-secondary" onClick={exportCSV}>Export CSV</button>
              <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
            </div>
          </div>

          {/* TWO ADMIN TABS REPORTED "EDUCATION STATUS" AND DISAGREED.
              This one said 28 registered users, 1 certified, 3.6% complete, 0% average
              score. The pilot tab said 8 opted in of 22 invited. Both were on screen, one
              click apart, and only one of them was about the course that exists.

              The four tiles here were computed by GET /api/admin/analytics from
              `users.completed_modules` and `users.quiz_scores` — the 36-module curriculum
              this file has held VOIDED since 2026-08-19 (see LEGACY_MODULES_VOIDED at the
              top). "1 certified" was an artefact of the Thinkific importer writing a
              synthetic range(1,37); "0% average score" was the true average of a field
              nobody has written since the course was rebuilt as chapters and lessons.

              So they are not scoped down — there is nothing under them to scope. Education
              on this tab is now READ FROM THE PILOT'S OWN ENDPOINT, the same
              /api/education/admin/tracker the tab beside it reads, through the same
              reducer. The two surfaces cannot disagree because there is now one source.

              `total_users` is kept: 28 registered accounts is a real fact about accounts
              and is not in the tracker, which knows only the 22 staged for the pilot. It is
              labelled "accounts" so it cannot be read as course progress. */}
          <div className="stats-grid stats-grid-narrow">
            <div className="stat-card">
              <div className="stat-value">{adminData.total_users}</div>
              <div className="stat-label">Registered accounts</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{groups?.length || 0}</div>
              <div className="stat-label">Groups</div>
            </div>
          </div>

          <PilotOptInCard onOpen={() => setActiveTab("pilot")} />

          <p className="admin-note" data-testid="overview-education-note">
            Course progress, quiz results and certification all live on{" "}
            <button className="btn btn-text btn-sm" onClick={() => setActiveTab("pilot")}>
              Pilot Invites &amp; Opt-ins
            </button>
            . The retired 36-module figures that used to sit here measured a curriculum the
            course no longer uses.
          </p>
        </>
      )}

      {activeTab === "users" && adminData && (
        <>
          <div className="admin-header">
            <div>
              <div className="section-title">All Users ({adminData.users?.length || 0})</div>
              {LEGACY_MODULES_VOIDED && (
                <p className="section-sub">
                  Every registered account. Course progress is on{" "}
                  <button className="btn btn-text btn-sm" onClick={() => setActiveTab("pilot")}>
                    Pilot Invites &amp; Opt-ins
                  </button>
                  .
                </p>
              )}
            </div>
            <div className="admin-actions">
              <button className="btn btn-secondary" onClick={exportCSV}>Export CSV</button>
            </div>
          </div>
          
          {selectedUser ? (
            <div className="user-detail-panel">
              <button className="btn btn-text" onClick={() => setSelectedUser(null)}>← Back to list</button>
              <div className="user-detail-header">
                <div className="user-detail-avatar">
                  {selectedUser.picture ? <img src={selectedUser.picture} alt="" /> : <span>{selectedUser.name?.charAt(0)}</span>}
                </div>
                <div>
                  <h2>{selectedUser.name}</h2>
                  <p>{selectedUser.email}</p>
                  {selectedUser.company && <p className="user-company">{selectedUser.company}</p>}
                </div>
              </div>
              {/* SAME VOIDED SOURCE AS THE OVERVIEW TILES. Modules, Progress and Avg Score
                  here are `completed_modules` and `quiz_scores` — the retired 36-module
                  curriculum — so this panel reported "0/36 · 0% · 0%" for people who had
                  genuinely worked through chapters of the real course. That is a third
                  admin surface disagreeing with the pilot roster, and it disagrees in the
                  worst direction: it says a working fitter has done nothing.

                  Gated on LEGACY_MODULES_VOIDED, the same constant the legacy nav item and
                  the legacy top-bar badge already respect, so the whole retired system
                  appears or disappears together. Flipping that one constant brings this
                  back with everything else. */}
              {!LEGACY_MODULES_VOIDED && (
                <>
                  <div className="user-detail-stats">
                    <div className="stat-card"><div className="stat-value">{selectedUser.completed_modules?.length || 0}/36</div><div className="stat-label">Modules</div></div>
                    <div className="stat-card"><div className="stat-value">{Math.round((selectedUser.completed_modules?.length || 0) / 36 * 100)}%</div><div className="stat-label">Progress</div></div>
                    <div className="stat-card"><div className="stat-value">{selectedUser.quiz_scores ? Math.round(Object.values(selectedUser.quiz_scores).reduce((a,b)=>a+b,0) / Object.keys(selectedUser.quiz_scores).length || 0) : 0}%</div><div className="stat-label">Avg Score</div></div>
                  </div>
                  <div className="user-detail-modules">
                    <h3>Module Progress</h3>
                    <div className="module-progress-grid">
                      {Array.from({length: 36}, (_, i) => i + 1).map(m => (
                        <div key={m} className={`module-dot ${selectedUser.completed_modules?.includes(m) ? 'completed' : ''}`} title={`Module ${m}`}>{m}</div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {LEGACY_MODULES_VOIDED && (
                <p className="admin-note">
                  Course progress for this person is on{" "}
                  <button className="btn btn-text btn-sm" onClick={() => setActiveTab("pilot")}>
                    Pilot Invites &amp; Opt-ins
                  </button>
                  , measured in the chapters and lessons they are actually taking.
                </p>
              )}
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                {/* The list of ALL accounts is this tab's unique value — the pilot roster
                    holds only the 22 staged for it — so the list stays. What goes is the
                    three columns fed by the voided 36-module fields: a progress bar out of
                    36, an average of a `quiz_scores` map nothing writes, and a "Certified"
                    badge that fires on completed_modules.length === 36 rather than on a
                    certificate. Joined, sign-in and View are account facts and are true. */}
                <thead>
                  <tr>
                    <th>Name</th><th>Email</th><th>Company</th>
                    {!LEGACY_MODULES_VOIDED && <><th>Progress</th><th>Score</th><th>Status</th></>}
                    {LEGACY_MODULES_VOIDED && <><th>Joined</th><th>Last sign-in</th></>}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {adminData.users?.map(u => (
                    <tr key={u.user_id}>
                      <td className="mod-title">{u.name}</td>
                      <td>{u.email}</td>
                      <td>{u.company || "—"}</td>
                      {!LEGACY_MODULES_VOIDED && (
                        <>
                          <td><div className="admin-progress-cell"><div className="admin-progress-bar"><div className="admin-progress-fill" style={{ width: `${u.progress_percent}%` }} /></div><span>{u.completed_modules}/36</span></div></td>
                          <td>{u.avg_score}%</td>
                          <td>{u.is_certified ? <span className="status-complete">✓ Certified</span> : <span className="status-available">In Progress</span>}</td>
                        </>
                      )}
                      {LEGACY_MODULES_VOIDED && (
                        <>
                          <td>{shortDate(u.created_at)}</td>
                          <td>{shortDate(u.last_login)}</td>
                        </>
                      )}
                      <td><button className="btn btn-text btn-sm" onClick={() => loadUserDetails(u.user_id)}>View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === "groups" && (
        <>
          <div className="section-title">User Groups</div>
          <p className="section-sub">Organize fitters by company or location</p>
          
          <div className="admin-card">
            <h3>Create New Group</h3>
            <div className="group-form">
              <input type="text" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Group name" className="bulk-email-input" />
              <input type="text" value={newGroupDesc} onChange={e => setNewGroupDesc(e.target.value)} placeholder="Description (optional)" className="bulk-email-input" />
              <button className="btn btn-primary" onClick={createGroup} disabled={!newGroupName.trim()}>Create Group</button>
            </div>
          </div>
          
          <div className="groups-list">
            {adminData?.groups?.map(g => (
              <div key={g.group_id} className="group-card">
                <div className="group-info">
                  <h4>{g.name}</h4>
                  <p>{g.description || "No description"}</p>
                  <span className="group-count">{g.member_count || 0} members</span>
                </div>
                <button className="btn btn-danger btn-sm" onClick={() => deleteGroup(g.group_id)}>Delete</button>
              </div>
            ))}
            {(!adminData?.groups || adminData.groups.length === 0) && (
              <p className="no-groups">No groups created yet</p>
            )}
          </div>
        </>
      )}

      {activeTab === "import" && (
        <>
          <div className="section-title">Import Users & Progress</div>
          <p className="section-sub">Upload CSV files to import users and their progress from Thinkific</p>
          
          <div className="admin-card">
            <h3>User Import</h3>
            <p className="import-info">Upload a CSV with columns: First Name, Last Name, Email, Company (optional)</p>
            <input type="file" accept=".csv" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} />
            <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()} disabled={importing}>
              {importing ? "Importing..." : "Choose CSV File"}
            </button>
            
            {importResult && (
              <div className={`import-result ${importResult.error ? 'error' : 'success'}`}>
                {importResult.error ? (
                  <p>{importResult.error}</p>
                ) : (
                  <>
                    <p>✓ Imported {importResult.imported} users</p>
                    <p>Skipped {importResult.skipped} (already exist)</p>
                    {importResult.errors?.length > 0 && (
                      <div className="import-errors">
                        <p>Errors:</p>
                        {importResult.errors.map((e, i) => <p key={i}>{e}</p>)}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="admin-card">
            <h3>Progress Import</h3>
            <p className="import-info">Upload a Thinkific progress export CSV to update user completion status</p>
            <ProgressImporter loadAdminData={loadAdminData} />
          </div>
        </>
      )}

      {activeTab === "email" && (
        <>
          <div className="section-title">Bulk Email</div>
          <p className="section-sub">Prepare a message for approval. Nothing sends until it is approved.</p>
          
          <div className="admin-card">
            <div className="bulk-email-form">
              <div className="bulk-email-row">
                <label>Recipients:</label>
                <select value={emailFilter} onChange={e => setEmailFilter(e.target.value)} className="bulk-email-select">
                  <option value="all">All Fitters</option>
                  <option value="inactive">Inactive (7+ days)</option>
                  <option value="incomplete">Incomplete Training</option>
                </select>
              </div>
              <div className="bulk-email-row">
                <label>Subject:</label>
                <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Email subject" className="bulk-email-input" />
              </div>
              <div className="bulk-email-row">
                <label>Message:</label>
                <textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)} placeholder="Email message (HTML supported)" className="bulk-email-textarea" rows={6} />
              </div>
              <div className="bulk-email-actions">
                <button className="btn btn-primary" onClick={sendBulkEmail} disabled={sendingBulk}>{sendingBulk ? "Preparing..." : "Prepare for approval"}</button>
                <button className="btn btn-secondary" onClick={sendReminders} disabled={sendingReminders}>{sendingReminders ? "Preparing..." : "Prepare reminders for approval"}</button>
              </div>
              {bulkResult && <div className="bulk-email-result">{bulkResult.message}</div>}
            </div>
          </div>
          <MailCampaigns apiUrl={API_URL} refreshKey={campaignRefresh} />
        </>
      )}

      {activeTab === "announcements" && (
        <AnnouncementsTab announcements={announcements}
                          loadAnnouncements={loadAnnouncements} />
      )}

      {activeTab === "videos" && (
        <VideosTab />
      )}

      {activeTab === "pilot" && <PilotTracker user={user} />}

      {activeTab === "settings" && (
        <>
          <div className="section-title">Admin Settings</div>
          <p className="section-sub">Manage administrators and platform settings</p>
          
          <div className="admin-card">
            <h3>Admin Accounts</h3>
            <p className="settings-info">Users with these emails have full admin access</p>
            
            <div className="admin-list">
              {adminEmails.map(email => (
                <div key={email} className="admin-list-item">
                  <span>{email}</span>
                  {adminEmails.length > 1 && (
                    <button className="btn btn-text btn-danger" onClick={() => removeAdmin(email)}>Remove</button>
                  )}
                </div>
              ))}
            </div>
            
            <div className="add-admin-form">
              <input type="email" value={newAdminEmail} onChange={e => setNewAdminEmail(e.target.value)} placeholder="New admin email" className="bulk-email-input" />
              <button className="btn btn-primary" onClick={addAdmin} disabled={!newAdminEmail.trim()}>Add Admin</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// HOISTED — Stage 2. Tier 1: measured to lose typed data when remounted.
function Settings({ user, completedModules, quizScores, handleLogout, resetProgress }) {
  const [resetConfirm, setResetConfirm] = useState(false);
  const [profile, setProfile] = useState({
    full_name: user?.name || "",
    phone: "",
    company: "",
    facility: "",
    city: "",
    bio: "",
    website: "",
    social_media: {
      twitter: "",
      facebook: "",
      instagram: "",
      linkedin: "",
      youtube: "",
      tiktok: ""
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  async function loadProfile() {
    try {
      const res = await fetch(`${API_URL}/api/profile`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setProfile({
          full_name: data.full_name || "",
          phone: data.phone || "",
          company: data.company || "",
          facility: data.facility || "",
          city: data.city || "",
          bio: data.bio || "",
          website: data.website || "",
          social_media: {
            twitter: data.social_media?.twitter || "",
            facebook: data.social_media?.facebook || "",
            instagram: data.social_media?.instagram || "",
            linkedin: data.social_media?.linkedin || "",
            youtube: data.social_media?.youtube || "",
            tiktok: data.social_media?.tiktok || ""
          }
        });
      }
    } catch (err) {
      console.error("Failed to load profile:", err);
    }
    setLoading(false);
  }

  async function saveProfile() {
    setSaving(true);
    setSaved(false);
    try {
      const res = await fetch(`${API_URL}/api/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profile)
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      console.error("Failed to save profile:", err);
    }
    setSaving(false);
  }

  function updateSocialMedia(platform, value) {
    setProfile(prev => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [platform]: value
      }
    }));
  }

  const socialPlatforms = [
    { key: "twitter", label: "Twitter / X", icon: "𝕏", placeholder: "https://twitter.com/yourusername" },
    { key: "facebook", label: "Facebook", icon: "f", placeholder: "https://facebook.com/yourpage" },
    { key: "instagram", label: "Instagram", icon: "📷", placeholder: "https://instagram.com/yourusername" },
    { key: "linkedin", label: "LinkedIn", icon: "in", placeholder: "https://linkedin.com/in/yourprofile" },
    { key: "youtube", label: "YouTube", icon: "▶", placeholder: "https://youtube.com/@yourchannel" },
    { key: "tiktok", label: "TikTok", icon: "♪", placeholder: "https://tiktok.com/@yourusername" }
  ];

  return (
    <div className="content settings-page" data-testid="settings">
      <div className="section-title">Settings & Profile</div>
      <p className="section-sub">Manage your account, profile, and social media connections.</p>
      
      <div className="settings-grid">
        {/* Profile Information */}
        <div className="admin-card">
          <h3>Profile Information</h3>
          {loading ? <p>Loading...</p> : (
            <>
              <div className="form-field">
                <label>Full Name</label>
                <input 
                  type="text" 
                  value={profile.full_name} 
                  onChange={e => setProfile({...profile, full_name: e.target.value})}
                  placeholder="Your full name"
                />
              </div>
              <div className="form-field">
                <label>Phone</label>
                <input 
                  type="tel" 
                  value={profile.phone} 
                  onChange={e => setProfile({...profile, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="form-field">
                <label>Company</label>
                <input 
                  type="text" 
                  value={profile.company} 
                  onChange={e => setProfile({...profile, company: e.target.value})}
                  placeholder="Your business name"
                />
              </div>
              {/* Facility and City are their own fields, not a re-use of Company: the Brand &
                  Marketing copy blocks fill [Facility] and [City] from them, and a business
                  name is not reliably the name a customer sees on the bay. Left empty, the
                  placeholder stays bracketed on that page rather than being guessed at. */}
              <div className="form-field">
                <label>Facility</label>
                <input
                  type="text"
                  value={profile.facility}
                  onChange={e => setProfile({...profile, facility: e.target.value})}
                  placeholder="Golf club or facility name"
                  data-testid="profile-facility"
                />
                <p className="field-hint">Fills [Facility] in your Brand &amp; Marketing copy blocks.</p>
              </div>
              <div className="form-field">
                <label>City</label>
                <input
                  type="text"
                  value={profile.city}
                  onChange={e => setProfile({...profile, city: e.target.value})}
                  placeholder="Coeur d'Alene"
                  data-testid="profile-city"
                />
                <p className="field-hint">Fills [City] in your Brand &amp; Marketing copy blocks.</p>
              </div>
              <div className="form-field">
                <label>Website</label>
                <input 
                  type="url" 
                  value={profile.website} 
                  onChange={e => setProfile({...profile, website: e.target.value})}
                  placeholder="https://yourwebsite.com"
                />
              </div>
              <div className="form-field">
                <label>Bio</label>
                <textarea 
                  value={profile.bio} 
                  onChange={e => setProfile({...profile, bio: e.target.value})}
                  placeholder="Tell us about yourself and your fitting experience..."
                  rows={3}
                />
              </div>
            </>
          )}
        </div>

        {/* Social Media Connections */}
        <div className="admin-card social-media-card">
          <h3>Social Media Accounts</h3>
          <p className="card-sub">Connect your social accounts to easily share content and grow your fitting business.</p>
          
          {loading ? <p>Loading...</p> : (
            <div className="social-links-form">
              {socialPlatforms.map(platform => (
                <div key={platform.key} className="social-link-field">
                  <div className="social-icon-label">
                    <span className={`social-icon ${platform.key}`}>{platform.icon}</span>
                    <span>{platform.label}</span>
                  </div>
                  <input 
                    type="url" 
                    value={profile.social_media[platform.key]} 
                    onChange={e => updateSocialMedia(platform.key, e.target.value)}
                    placeholder={platform.placeholder}
                    data-testid={`social-${platform.key}`}
                  />
                  {profile.social_media[platform.key] && (
                    <a 
                      href={profile.social_media[platform.key]} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link-preview"
                    >
                      View →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <div className="save-section">
            <button 
              className="btn btn-primary" 
              onClick={saveProfile} 
              disabled={saving || loading}
              data-testid="save-profile-btn"
            >
              {saving ? "Saving..." : saved ? "✓ Saved!" : "Save Profile"}
            </button>
          </div>
        </div>

        {/* Account Info */}
        <div className="admin-card">
          <h3>Account Information</h3>
          <div className="admin-field">
            <div className="admin-field-label">Email</div>
            <div className="admin-field-value">{user.email}</div>
          </div>
          <div className="admin-field">
            <div className="admin-field-label">Account Type</div>
            <div className="admin-field-value">{user.is_admin ? "Administrator" : "Fitter"}</div>
          </div>
          <button className="btn btn-secondary" onClick={handleLogout} data-testid="logout-btn">
            Sign Out
          </button>
        </div>
        
        {/* Training Progress */}
        <div className="admin-card">
          <h3>Training Progress</h3>
          <div className="admin-field">
            <div className="admin-field-label">Modules Completed</div>
            <div className="admin-field-value highlight">{completedModules.length} / 36</div>
          </div>
          <div className="admin-field">
            <div className="admin-field-label">Average Score</div>
            <div className="admin-field-value highlight">
              {Object.values(quizScores).length ? `${Math.round(Object.values(quizScores).reduce((a, b) => a + b, 0) / Object.values(quizScores).length)}%` : "—"}
            </div>
          </div>
          <div className="divider" />
          {!resetConfirm ? (
            <button className="btn btn-danger" onClick={() => setResetConfirm(true)} data-testid="reset-btn">
              Reset All Progress
            </button>
          ) : (
            <div className="reset-confirm">
              <div className="reset-warning">
                This will erase all completed modules and quiz scores. Are you sure?
              </div>
              <div className="reset-actions">
                <button className="btn btn-danger" onClick={resetProgress} data-testid="confirm-reset-btn">Yes, Reset</button>
                <button className="btn btn-secondary" onClick={() => setResetConfirm(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// HOISTED — Stage 2 close-out. MEASURED Tier 1: its own success path called
// loadAdminData(), which changed App state and remounted it, wiping the import result
// before the admin could read it. The outcome of an import that DID happen was
// unreachable by design, not by accident.
function ProgressImporter({ loadAdminData }) {
  const [progressFile, setProgressFile] = useState(null);
  const [importingProgress, setImportingProgress] = useState(false);
  const [progressResult, setProgressResult] = useState(null);
  const progressFileRef = useRef(null);

  async function handleProgressUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportingProgress(true);
    setProgressResult(null);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/import-progress`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      
      if (res.ok) {
        const result = await res.json();
        setProgressResult(result);
        // Refresh analytics
        loadAdminData();
      } else {
        // SAY WHAT THE SERVER SAID. The importer is now refused outright while the 36-module
        // curriculum is void, and "Failed to import progress" reads as a transient fault an
        // admin would reasonably retry — which is the opposite of the truth. The refusal
        // names the replacement surface, so pass it through rather than flattening it.
        const d = await res.json().catch(() => ({}));
        setProgressResult({ error: d.detail || `Failed to import progress (${res.status})` });
      }
    } catch (err) {
      setProgressResult({ error: err.message });
    }
    
    setImportingProgress(false);
    e.target.value = "";
  }

  return (
    <>
      <input 
        type="file" 
        accept=".csv" 
        ref={progressFileRef} 
        onChange={handleProgressUpload} 
        style={{ display: 'none' }} 
      />
      <button 
        className="btn btn-primary" 
        onClick={() => progressFileRef.current?.click()} 
        disabled={importingProgress}
        data-testid="import-progress-btn"
      >
        {importingProgress ? "Importing Progress..." : "Choose Progress CSV"}
      </button>
      
      {progressResult && (
        <div className={`import-result ${progressResult.error ? 'error' : 'success'}`}>
          {progressResult.error ? (
            <p>{progressResult.error}</p>
          ) : (
            <>
              <p>✓ Updated progress for {progressResult.updated} users</p>
              <p>{progressResult.not_found} users not found in system</p>
              {progressResult.errors?.length > 0 && (
                <div className="import-errors">
                  <p>Errors:</p>
                  {progressResult.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

/**
 * WHAT AN EDUCATION-ONLY ACCOUNT MAY SEE. Presentation only — the server's default-deny
 * middleware is the gate and this list is not it. Kept beside the sidebar branch so the two
 * cannot drift apart.
 */
export const EDUCATION_ONLY_VIEWS = ["hg-education", "certificate", "settings"];

export function isEducationOnly(user) {
  return user?.role === "education_only";
}

/**
 * WHAT A REFUSED HUB VIEW LOOKS LIKE.
 *
 * MEASURED BEFORE THIS EXISTED: every loader in this file is `if (res.ok) { ...setState }` with
 * a catch that only logs. So a 403 was SILENTLY SWALLOWED — state stayed at its initial `[]`,
 * nothing threw, and the error boundary never saw anything. The student did not get a white
 * screen or a spinner; they got the Fitter Hub dashboard rendered in full, with zeros
 * everywhere and quick actions that did nothing. That is worse than a crash, because it looks
 * like a working product they own rather than one they have no access to.
 */
function EducationOnlyNotice({ onBack }) {
  return (
    <div className="edu-refusal" data-testid="education-only-notice"
         style={{ maxWidth: 560, margin: "72px auto", textAlign: "center", padding: "0 24px" }}>
      <h2 style={{ fontSize: 22, marginBottom: 10 }}>This account is for HG Education</h2>
      <p style={{ color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 22 }}>
        Your account has access to the Henry-Griffitts Education course. The rest of CoFitter —
        customers, fittings, events and marketing — is part of the Fitter Hub and is not
        included. Nothing has gone wrong.
      </p>
      <button className="edu-btn" onClick={onBack} data-testid="education-only-back">
        Go to HG Education
      </button>
    </div>
  );
}


export default function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authView, setAuthView] = useState("login"); // login, invite, forgot, reset
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null); // {email, name} from a valid invite
  const [authError, setAuthError] = useState("");
  const [authSuccess, setAuthSuccess] = useState("");
  const [resetToken, setResetToken] = useState("");
  
  // App state
  // LANDING IS ROLE-AWARE. `view` cannot be initialised from `user` because the user is not
  // resolved on first render, so the correction happens in an effect once it is. An
  // education-only account must never land on the Fitter Hub dashboard — that screen is a
  // different product, and it now refuses rather than rendering empty.
  // A LESSON ADDRESS LANDS IN THE COURSE, not on the dashboard. Randall's ruling 2026-08-22:
  // reload should return a student to the lesson they had open. The Education shell reads the
  // same path to decide WHICH lesson; this only decides which view hosts it. Client-side only.
  const [view, setView] = useState(
    () => (/^\/education\//.test(window.location.pathname) ? "hg-education" : "copilot"));
  const [currentModule, setCurrentModule] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [completedModules, setCompletedModules] = useState([]);
  const [quizScores, setQuizScores] = useState({});
  // Sidebar: open on desktop, closed (off-canvas drawer) on phones/tablets.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window === 'undefined' || window.innerWidth > 768
  );
  const closeSidebarOnMobile = () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) setSidebarOpen(false);
  };
  // Crossing the desktop breakpoint re-shows the sidebar (the toggle is hidden
  // on desktop, so a drawer left closed on mobile would otherwise be unreachable).
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    const onChange = (e) => { if (e.matches) setSidebarOpen(true); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  
  // Admin state
  const [adminData, setAdminData] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [groups, setGroups] = useState([]);
  
  // Email state
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  
  // Which admin tab is open. Held here rather than inside AdminAnalytics so the dashboard
  // card can open Admin ON the tracker instead of dropping the admin on Overview to find it.
  const [adminTab, setAdminTab] = useState(ADMIN_DEFAULT_TAB);

  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  
  // Slide videos state
  const [slideVideos, setSlideVideos] = useState({});

  // ==================== CO-PILOT STATE ====================
  const [copilotView, setCopilotView] = useState("dashboard"); // dashboard, randy, crm, fitting, events, territory, marketing, forum, fittingsystem, license

  // Role-aware landing and in-app refusal. Runs whenever the user or the view changes, so it
  // catches both the initial land and any later attempt to reach a Hub view.
  useEffect(() => {
    if (isEducationOnly(user) && !EDUCATION_ONLY_VIEWS.includes(view)) {
      setView("hg-education");
    }
  }, [user, view]);
  const [golfers, setGolfers] = useState([]);
  const [selectedGolfer, setSelectedGolfer] = useState(null);
  const [fittings, setFittings] = useState([]);
  const [selectedFitting, setSelectedFitting] = useState(null);
  const [events, setEvents] = useState([]);
  const [territory, setTerritory] = useState([]);
  const [copilotAnalytics, setCopilotAnalytics] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [outreachTemplates, setOutreachTemplates] = useState({});
  const [socialTemplates, setSocialTemplates] = useState([]);
  const [conversationStarters, setConversationStarters] = useState([]);
  
  // Education Gate State
  const [educationStatus, setEducationStatus] = useState(null);
  // The education track's own percent, for the sidebar bar above.
  const [eduTrackPercent, setEduTrackPercent] = useState(0);
  // PHASE 4 (2026-08-11): the REAL certification signal, from HG Education — every content
  // chapter complete AND a passed, non-voided final exam, computed server-side by
  // E.certification_eligibility. The Certificate nav item used to hang off the 36 LEGACY
  // modules, which nobody completes, so it never appeared for anyone.
  const [hgCertification, setHgCertification] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const hasProcessedAuth = useRef(false);

  // Check for password-reset or account-invite tokens in the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetParam = params.get("reset");
    if (resetParam) {
      setResetToken(resetParam);
      setAuthView("reset");
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }
    const inviteParam = params.get("invite");
    if (inviteParam) {
      setInviteToken(inviteParam);
      fetch(`${API_URL}/api/auth/invite/${inviteParam}`)
        .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
        .then((d) => { setInviteInfo(d); setAuthView("invite"); })
        .catch((e) => { setAuthError(e.detail || "This invite link is not valid."); setAuthView("login"); });
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  // Check auth on mount
  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        credentials: "include"
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        await loadProgress(userData);
      }
    } catch (err) {
      console.log("Not authenticated");
    }
    setAuthLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Load progress from server.
  //
  // TAKES THE USER AS AN ARGUMENT rather than closing over `user` state, because its first
  // caller is checkAuth, which runs BEFORE the state it just set has been applied. Reading
  // `user` here would see null on the very call where the role matters most.
  //
  // Of the three fetches, only certification eligibility is on the education allowlist;
  // /api/progress and /api/education/status both belong to the legacy Hub module system and
  // are refused for a student. So a student makes one request here instead of three.
  async function loadProgress(forUser) {
    const eduOnly = isEducationOnly(forUser);
    try {
      if (!eduOnly) {
        const res = await fetch(`${API_URL}/api/progress`, {
          credentials: "include"
        });
        if (res.ok) {
          const data = await res.json();
          setCompletedModules(data.completed_modules || []);
          setQuizScores(data.quiz_scores || {});
        }

        // Also load education status
        const eduRes = await fetch(`${API_URL}/api/education/status`, {
          credentials: "include"
        });
        if (eduRes.ok) {
          const eduData = await eduRes.json();
          setEducationStatus(eduData);
        }
      }

      // PHASE 4: HG Education certification eligibility. Failure is non-fatal — the Certificate
      // nav simply stays hidden, which is the safe direction for a credential.
      const certRes = await fetch(`${API_URL}/api/education/certification/eligibility`, {
        credentials: "include"
      });
      if (certRes.ok) {
        setHgCertification(await certRes.json());
      }

      // The course percent for the sidebar. /api/education/me is on the education allowlist,
      // so this is one call an education-only account may actually make.
      const meRes = await fetch(`${API_URL}/api/education/me`, { credentials: "include" });
      if (meRes.ok) {
        const md = await meRes.json();
        const enrolled = (md.tracks || []).find((t) => t.enrolled) || (md.tracks || [])[0];
        setEduTrackPercent(Math.round(enrolled?.percent || 0));
      }
    } catch (err) {
      console.error("Failed to load progress:", err);
    }
  }

  // Save progress to server
  async function saveProgress(newCompleted, newScores) {
    try {
      await fetch(`${API_URL}/api/progress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          completed_modules: newCompleted,
          quiz_scores: newScores
        })
      });
    } catch (err) {
      console.error("Failed to save progress:", err);
    }
  }

  // Auth handlers
  async function handleLogin(e) {
    e.preventDefault();
    setAuthError("");
    const form = e.target;
    const email = form.email.value;
    const password = form.password.value;
    
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password })
      });
      
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        await loadProgress(userData);
      } else {
        const error = await res.json();
        setAuthError(error.detail || "Login failed");
      }
    } catch (err) {
      setAuthError("Network error. Please try again.");
    }
  }

  async function handleAcceptInvite(e) {
    e.preventDefault();
    setAuthError("");
    const form = e.target;
    const password = form.password.value;
    if (password.length < 8) {
      setAuthError("Password must be at least 8 characters");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/auth/accept-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token: inviteToken, password, name: form.name.value })
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
      } else {
        const error = await res.json();
        setAuthError(error.detail || "Could not set up the account");
      }
    } catch (err) {
      setAuthError("Network error. Please try again.");
    }
  }


  async function handleLogout() {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch (err) {
      console.error("Logout error:", err);
    }
    setUser(null);
    setCompletedModules([]);
    setQuizScores({});
    setView("dashboard");
  }

  // Forgot password handler
  async function handleForgotPassword(e) {
    e.preventDefault();
    setAuthError("");
    setAuthSuccess("");
    const email = e.target.email.value;
    
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setAuthSuccess("If an account exists with this email, you'll receive a password reset link.");
      } else {
        // THE SERVER'S OWN WORDS (2026-11-02): a fitter is never told a link is coming when it is not.
        setAuthError(body.detail || "We could not send a reset link. Please contact Henry-Griffitts.");
      }
    } catch (err) {
      setAuthError("Network error. Please try again.");
    }
  }

  // Reset password handler
  async function handleResetPassword(e) {
    e.preventDefault();
    setAuthError("");
    setAuthSuccess("");
    const newPassword = e.target.password.value;
    const confirmPassword = e.target.confirmPassword.value;
    
    if (newPassword !== confirmPassword) {
      setAuthError("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 6) {
      setAuthError("Password must be at least 6 characters");
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, new_password: newPassword })
      });
      
      if (res.ok) {
        setAuthSuccess("Password reset successfully! You can now sign in.");
        setAuthView("login");
        setResetToken("");
      } else {
        const error = await res.json();
        setAuthError(error.detail || "Failed to reset password");
      }
    } catch (err) {
      setAuthError("Network error. Please try again.");
    }
  }

  // Admin functions
  // useCallback so the admin effect can name it as a dependency honestly rather than
  // omitting it. A plain function is rebuilt every render, so listing it would loop.
  const loadAdminData = useCallback(async () => {
    if (!user?.is_admin) return;
    setAdminLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setAdminData(data);
        setGroups(data.groups || []);
      }
    } catch (err) {
      console.error("Failed to load admin data:", err);
    }
    setAdminLoading(false);
  }, [user?.is_admin]);

  async function loadGroups() {
    try {
      const res = await fetch(`${API_URL}/api/groups`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setGroups(data.groups || []);
      }
    } catch (err) {
      console.error("Failed to load groups:", err);
    }
  }

  async function loadUserDetails(userId) {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSelectedUser(data);
      }
    } catch (err) {
      console.error("Failed to load user details:", err);
    }
  }

  async function loadComments(moduleId) {
    try {
      const res = await fetch(`${API_URL}/api/modules/${moduleId}/comments`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error("Failed to load comments:", err);
    }
  }

  async function postComment(moduleId, content, parentId = null) {
    try {
      const res = await fetch(`${API_URL}/api/modules/${moduleId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ module_id: moduleId, content, parent_id: parentId })
      });
      if (res.ok) {
        setNewComment("");
        setReplyingTo(null);
        loadComments(moduleId);
      }
    } catch (err) {
      console.error("Failed to post comment:", err);
    }
  }

  async function deleteComment(commentId) {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await fetch(`${API_URL}/api/comments/${commentId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (currentModule) loadComments(currentModule.id);
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  }

  async function loadAnnouncements() {
    try {
      const res = await fetch(`${API_URL}/api/announcements`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (err) {
      console.error("Failed to load announcements:", err);
    }
  }

  async function loadSlideVideos() {
    try {
      const res = await fetch(`${API_URL}/api/slide-videos`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSlideVideos(data.videos || {});
      }
    } catch (err) {
      console.error("Failed to load slide videos:", err);
    }
  }

  // ==================== CO-PILOT DATA FUNCTIONS ====================
  
  async function loadCopilotAnalytics() {
    try {
      const res = await fetch(`${API_URL}/api/cofitter/analytics`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCopilotAnalytics(data);
      }
    } catch (err) {
      console.error("Failed to load copilot analytics:", err);
    }
  }

  async function loadGolfers(search = "") {
    try {
      const url = search 
        ? `${API_URL}/api/cofitter/golfers?search=${encodeURIComponent(search)}`
        : `${API_URL}/api/cofitter/golfers`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setGolfers(data.golfers || []);
      }
    } catch (err) {
      console.error("Failed to load golfers:", err);
    }
  }

  async function loadFittings(golferId = null) {
    try {
      const url = golferId 
        ? `${API_URL}/api/cofitter/fittings?golfer_id=${golferId}`
        : `${API_URL}/api/cofitter/fittings`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setFittings(data.fittings || []);
      }
    } catch (err) {
      console.error("Failed to load fittings:", err);
    }
  }

  async function loadEvents() {
    try {
      const res = await fetch(`${API_URL}/api/cofitter/events`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error("Failed to load events:", err);
    }
  }

  async function loadTerritory() {
    try {
      const res = await fetch(`${API_URL}/api/cofitter/territory`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTerritory(data.facilities || []);
      }
    } catch (err) {
      console.error("Failed to load territory:", err);
    }
  }

  async function loadMarketingTemplates() {
    try {
      const [outreach, social, scripts] = await Promise.all([
        fetch(`${API_URL}/api/cofitter/templates/outreach`, { credentials: "include" }),
        fetch(`${API_URL}/api/cofitter/templates/social`, { credentials: "include" }),
        fetch(`${API_URL}/api/cofitter/scripts`, { credentials: "include" })
      ]);
      
      if (outreach.ok) {
        const data = await outreach.json();
        setOutreachTemplates(data.templates || {});
      }
      if (social.ok) {
        const data = await social.json();
        setSocialTemplates(data.templates || []);
      }
      if (scripts.ok) {
        const data = await scripts.json();
        setConversationStarters(data.conversation_starters || []);
      }
    } catch (err) {
      console.error("Failed to load marketing templates:", err);
    }
  }

  async function sendChatMessage() {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setChatLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/cofitter/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: userMessage,
          session_id: chatSessionId,
          context: "general"
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, { role: "assistant", content: data.response }]);
        if (data.session_id) setChatSessionId(data.session_id);
      } else {
        setChatMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble responding right now. Please try again." }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: "assistant", content: "Connection error. Please try again." }]);
    }
    
    setChatLoading(false);
  }

  // FITTER HUB DATA, FOR FITTERS. Every loader below reaches a route an education-only
  // account is refused, so firing them for a student produced five guaranteed 403s on every
  // login. The refusal working is not a reason to keep asking: the request should never have
  // been made, and the console full of red was only how that announced itself.
  //
  // This is a COURTESY, NOT A CONTROL. The server's default-deny is the gate; not asking is
  // just not wasting a round trip on an answer we already know.
  useEffect(() => {
    if (user && !isEducationOnly(user)) {
      loadAnnouncements();
      loadSlideVideos();
      // Load CoFitter data
      loadCopilotAnalytics();
      loadGolfers();
      loadMarketingTemplates();
    }
  }, [user]);

  useEffect(() => {
    if (view === "admin" && user?.is_admin) {
      loadAdminData();
    }
  }, [view, user, loadAdminData]);

  useEffect(() => {
    if (currentModule && view === "module") {
      loadComments(currentModule.id);
    }
  }, [currentModule, view]);

  // Email certificate
  async function sendCertificateEmail() {
    if (!user || !allLegacyModulesComplete) return;
    setEmailSending(true);
    
    const avg = Object.values(quizScores).length
      ? Math.round(Object.values(quizScores).reduce((a, b) => a + b, 0) / Object.values(quizScores).length)
      : 100;
    
    try {
      const res = await fetch(`${API_URL}/api/certificate/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          recipient_email: user.email,
          user_name: user.name,
          completion_date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
          avg_score: avg
        })
      });
      
      if (res.ok) {
        setEmailSent(true);
      } else {
        alert("Failed to send email. Please try again.");
      }
    } catch (err) {
      alert("Failed to send email. Please try again.");
    }
    setEmailSending(false);
  }

  // COURSE PROGRESS, FOR THE COURSE THE USER IS ACTUALLY TAKING.
  //
  // This was `completedModules.length / MODULES.length` for everyone -- the LEGACY 36-module
  // system. An HG Education student never touches those modules, so the bar read 0% while
  // chapter 1 sat there with six ticks on it, because the two numbers come from different
  // systems entirely (edu_completions vs the legacy progress doc). Same empty-set-reads-as-a
  // -real-number family as the three trackProgressState bugs.
  //
  // CORRECTED AGAIN 2026-09-01. "Everyone else keeps the legacy number" was still wrong for the
  // person most likely to be walking the course: Randall is an ADMIN, not education-only, so he
  // got the legacy figure — a percentage of a 36-module curriculum that has been retired — while
  // his real chapters ticked along unseen. He reported it as "0/36 COMPLETE" on his own
  // walk-through, which is the third time this family of bug has surfaced.
  //
  // The legacy system is VOIDED. While it is, NOBODY's progress should be reported against it,
  // so the track percent is the number for everyone; the legacy figure survives only inside the
  // admin view of the old system itself.
  const legacyProgress = Math.round((completedModules.length / MODULES.length) * 100);
  const totalProgress = (LEGACY_MODULES_VOIDED || isEducationOnly(user))
    ? eduTrackPercent
    : legacyProgress;
  const allLegacyModulesComplete = completedModules.length === MODULES.length;

  function isModuleLocked(moduleId) {
    // Admins can access any module
    if (user?.is_admin) return false;
    if (moduleId === 1) return false;
    return !completedModules.includes(moduleId - 1);
  }

  function isModuleCompleted(moduleId) {
    return completedModules.includes(moduleId);
  }

  function openModule(mod) {
    if (isModuleLocked(mod.id)) return;
    setCurrentModule(mod);
    setCurrentSlide(0);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setView("module");
  }

  function goToQuiz() {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setView("quiz");
  }

  function submitQuiz() {
    const q = currentModule.quiz;
    let correct = 0;
    q.forEach((item, i) => {
      if (quizAnswers[i] === item.answer) correct++;
    });
    const score = Math.round((correct / q.length) * 100);
    setQuizScore(score);
    setQuizSubmitted(true);
    
    const newScores = { ...quizScores, [currentModule.id]: score };
    setQuizScores(newScores);
    
    if (score >= PASS_SCORE) {
      const newCompleted = completedModules.includes(currentModule.id)
        ? completedModules
        : [...completedModules, currentModule.id];
      setCompletedModules(newCompleted);
      saveProgress(newCompleted, newScores);
    } else {
      saveProgress(completedModules, newScores);
    }
  }

  function retakeQuiz() {
    setQuizAnswers({});
    setQuizSubmitted(false);
  }

  function nextModule() {
    const next = MODULES.find((m) => m.id === currentModule.id + 1);
    if (next) {
      openModule(next);
    } else {
      setView("certificate");
    }
  }

  async function resetProgress() {
    try {
      await fetch(`${API_URL}/api/progress/reset`, {
        method: "POST",
        credentials: "include"
      });
      setCompletedModules([]);
      setQuizScores({});
      setView("dashboard");
    } catch (err) {
      console.error("Failed to reset progress:", err);
    }
  }

  // Auth Screen
  if (authLoading) {
    return (
      <div className="auth-loading">
        <img src="/images/hg-logo.png" alt="Henry-Griffitts" className="auth-loading-logo" />
        <div className="auth-loading-text">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <img src="/images/hg-logo.png" alt="Henry-Griffitts" className="auth-logo" />
          <h1 className="auth-title">Certified Fitter Hub</h1>
          <p className="auth-subtitle">
            {authView === "forgot" ? "Reset your password" : 
             authView === "reset" ? "Create new password" :
             "Access your business tools and certification"}
          </p>
          
          {authError && <div className="auth-error">{authError}</div>}
          {authSuccess && <div className="auth-success">{authSuccess}</div>}
          
          {authView === "login" && (
            <>
              <form onSubmit={handleLogin} className="auth-form">
                <input type="email" name="email" placeholder="Email address" required className="auth-input" data-testid="login-email" />
                <input type="password" name="password" placeholder="Password" required className="auth-input" data-testid="login-password" />
                <button type="submit" className="btn btn-primary auth-btn" data-testid="login-submit">Sign In</button>
              </form>
              <button 
                className="auth-forgot-link"
                onClick={() => { setAuthView("forgot"); setAuthError(""); setAuthSuccess(""); }}
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </>
          )}
          
          {authView === "invite" && inviteInfo && (
            <form onSubmit={handleAcceptInvite} className="auth-form">
              <p className="auth-subtitle" data-testid="invite-note">Setting up the account for <b>{inviteInfo.email}</b></p>
              <input type="text" name="name" defaultValue={inviteInfo.name} placeholder="Full name" required className="auth-input" data-testid="invite-name" />
              <input type="password" name="password" placeholder="Create a password (min 8 chars)" required className="auth-input" data-testid="invite-password" />
              <button type="submit" className="btn btn-primary auth-btn" data-testid="invite-submit">Set up my account</button>
            </form>
          )}
          
          {authView === "forgot" && (
            <form onSubmit={handleForgotPassword} className="auth-form">
              <input type="email" name="email" placeholder="Email address" required className="auth-input" data-testid="forgot-email" />
              <button type="submit" className="btn btn-primary auth-btn" data-testid="forgot-submit">Send Reset Link</button>
              <button 
                type="button"
                className="btn btn-secondary auth-btn"
                onClick={() => { setAuthView("login"); setAuthError(""); setAuthSuccess(""); }}
              >
                Back to Sign In
              </button>
            </form>
          )}
          
          {authView === "reset" && (
            <form onSubmit={handleResetPassword} className="auth-form">
              <input type="password" name="password" placeholder="New password (min 6 chars)" required className="auth-input" data-testid="reset-password" />
              <input type="password" name="confirmPassword" placeholder="Confirm new password" required className="auth-input" data-testid="reset-confirm" />
              <button type="submit" className="btn btn-primary auth-btn" data-testid="reset-submit">Reset Password</button>
            </form>
          )}
          
          {authView === "login" && (
            <>
              <div className="auth-switch">
                <span data-testid="invite-only-note">Accounts are invite-only — new fitters receive a private setup link from Henry-Griffitts.</span>
              </div>
              
              <div className="auth-switch">
                <span>Interested in becoming a fitter? Visit{' '}
                  <a href="https://api.hub.henry-griffitts.com/api/public/become-a-fitter?src=cofitter" data-testid="become-fitter-link">
                    Become a Henry-Griffitts Fitter</a>.
                </span>
              </div>
            </>
          )}
        </div>
        
        {/* Onboarding Modal */}
        {showOnboarding && (
          <div className="onboarding-modal">
            <OnboardingFlow 
              onComplete={() => setShowOnboarding(false)}
              onCancel={() => setShowOnboarding(false)}
            />
          </div>
        )}
      </div>
    );
  }

  // Sidebar Component


  // ==================== CO-PILOT COMPONENTS ====================

  // CoFitter Chat Component
  // `messages` is a PROP, not a closure read. eslint was right to object: chatMessages
  // belongs to App, so listing it here named a value this component cannot react to.
  function RandyChat({ messages }) {
    const chatEndRef = useRef(null);

    useEffect(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    return (
      <div className="copilot-content randy-chat" data-testid="randy-chat">
        <div className="copilot-header">
          <h2><CoPilotIcon type="randy" /> CoFitter</h2>
          <p>Your AI-powered fitting assistant</p>
        </div>
        
        <div className="chat-container">
          <div className="chat-messages">
            {chatMessages.length === 0 && (
              <div className="chat-welcome">
                <HGMark size={38} />
                <h3>Hi, I'm CoFitter!</h3>
                <p>I'm here to help with fitting questions, product info, lesson-to-fitting conversions, and more. What can I help you with today?</p>
                <div className="quick-prompts">
                  <button onClick={() => setChatInput("How do I start a fitting conversation with a golfer?")} className="quick-prompt">Fitting conversation tips</button>
                  <button onClick={() => setChatInput("What are the key things to check during a lie angle fitting?")} className="quick-prompt">Lie angle basics</button>
                  <button onClick={() => setChatInput("How do I convert a lesson into a fitting opportunity?")} className="quick-prompt">Lesson to fitting</button>
                  <button onClick={() => setChatInput("Tell me about Henry-Griffitts iron options")} className="quick-prompt">Product info</button>
                </div>
              </div>
            )}
            
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role}`}>
                <div className="message-avatar">
                  {msg.role === "user" ? <CircleUser size={20} strokeWidth={1.7} /> : <HGMark size={26} />}
                </div>
                <div className="message-content">
                  {msg.content.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </div>
            ))}
            
            {chatLoading && (
              <div className="chat-message assistant">
                <div className="message-avatar"><HGMark size={26} /></div>
                <div className="message-content typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          
          <div className="chat-input-area">
            <input
              type="text"
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendChatMessage()}
              placeholder="Ask CoFitter anything about fitting, products, or business..."
              disabled={chatLoading}
              data-testid="randy-input"
            />
            <button 
              onClick={sendChatMessage} 
              disabled={!chatInput.trim() || chatLoading}
              data-testid="randy-send"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    );
  }

  // CRM/Customers Component

  // Fitting Workflow Component

  // Events Component  

  // Territory Component

  // Marketing Component
  function MarketingView() {
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [userSocials, setUserSocials] = useState({});
    const [shareContent, setShareContent] = useState("");
    const [showShareModal, setShowShareModal] = useState(false);

    useEffect(() => {
      loadUserSocials();
    }, []);

    async function loadUserSocials() {
      try {
        const res = await fetch(`${API_URL}/api/profile`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setUserSocials(data.social_media || {});
        }
      } catch (err) {
        console.error("Failed to load socials:", err);
      }
    }

    function shareToSocial(platform, content) {
      const encodedContent = encodeURIComponent(content);
      const shareUrls = {
        twitter: `https://twitter.com/intent/tweet?text=${encodedContent}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?quote=${encodedContent}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=&summary=${encodedContent}`,
      };
      
      if (shareUrls[platform]) {
        window.open(shareUrls[platform], '_blank', 'width=600,height=400');
      }
    }

    const connectedPlatforms = Object.entries(userSocials).filter(([_, url]) => url);

    return (
      <div className="copilot-content marketing-view" data-testid="marketing-view">
        <div className="copilot-header">
          <h2><CoPilotIcon type="marketing" /> Marketing & Outreach</h2>
          <p>Templates and tools for growing your business</p>
        </div>

        {/* Quick Share Section */}
        <div className="marketing-section share-section">
          <h3><Megaphone size={16} strokeWidth={1.8} style={{verticalAlign:"-3px",marginRight:8,color:"var(--hg-green)"}} />Quick Share to Social</h3>
          <p className="section-desc">Share content directly to your connected social accounts</p>
          
          {connectedPlatforms.length > 0 ? (
            <div className="quick-share-box">
              <textarea 
                value={shareContent}
                onChange={e => setShareContent(e.target.value)}
                placeholder="Write your post here..."
                rows={3}
              />
              <div className="share-buttons">
                {userSocials.twitter && (
                  <button className="share-btn twitter" onClick={() => shareToSocial('twitter', shareContent)}>
                    <span className="share-icon">𝕏</span> Share to Twitter
                  </button>
                )}
                {userSocials.facebook && (
                  <button className="share-btn facebook" onClick={() => shareToSocial('facebook', shareContent)}>
                    <span className="share-icon">f</span> Share to Facebook
                  </button>
                )}
                {userSocials.linkedin && (
                  <button className="share-btn linkedin" onClick={() => shareToSocial('linkedin', shareContent)}>
                    <span className="share-icon">in</span> Share to LinkedIn
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="connect-socials-prompt">
              <p>Connect your social media accounts in Settings to enable quick sharing!</p>
              <button className="btn btn-primary" onClick={() => { setView("settings"); }}>
                Connect Accounts
              </button>
            </div>
          )}
        </div>

        <div className="marketing-sections">
          <div className="marketing-section">
            <h3><Megaphone size={16} strokeWidth={1.8} style={{verticalAlign:"-3px",marginRight:8,color:"var(--hg-green)"}} />Brand &amp; Marketing</h3>
            <p className="section-desc">
              Your certification badge, the HG logo files, and the pre-approved copy blocks from
              the Fitter Marketing Kit — plus the four rules for using the Henry-Griffitts name.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setCopilotView("brandmarketing")}
              data-testid="open-brand-marketing"
            >
              Open Brand &amp; Marketing
            </button>
          </div>

          <div className="marketing-section">
            <h3><MessagesSquare size={16} strokeWidth={1.8} style={{verticalAlign:"-3px",marginRight:8,color:"var(--hg-green)"}} />Conversation Starters</h3>
            <div className="starters-list">
              {conversationStarters.map((starter, idx) => (
                <div key={idx} className="starter-card">
                  <p>"{starter}"</p>
                  <button className="btn btn-text" onClick={() => navigator.clipboard.writeText(starter)}>Copy</button>
                </div>
              ))}
            </div>
          </div>

          <div className="marketing-section">
            <h3><Megaphone size={16} strokeWidth={1.8} style={{verticalAlign:"-3px",marginRight:8,color:"var(--hg-green)"}} />Email Templates</h3>
            <div className="templates-grid">
              {Object.entries(outreachTemplates).map(([key, template]) => (
                <div key={key} className="template-card" onClick={() => setSelectedTemplate({ key, ...template })}>
                  <h4>{key.replace(/_/g, ' ')}</h4>
                  <p className="template-preview">{fillTemplateExample(template.subject)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="marketing-section">
            <h3><MessagesSquare size={16} strokeWidth={1.8} style={{verticalAlign:"-3px",marginRight:8,color:"var(--hg-green)"}} />Social Media Posts</h3>
            <p className="section-desc">Ready-to-use templates - click to copy or share directly</p>
            <div className="social-templates">
              {socialTemplates.map((template, idx) => (
                <div key={idx} className="social-card">
                  <span className="social-type">{template.type}</span>
                  <p>{template.text}</p>
                  <div className="social-card-actions">
                    <button className="btn btn-text" onClick={() => navigator.clipboard.writeText(template.text)}>Copy</button>
                    {userSocials.twitter && (
                      <button className="btn btn-text" onClick={() => shareToSocial('twitter', template.text)}>Share →</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {selectedTemplate && (
          <div className="modal-overlay" onClick={() => setSelectedTemplate(null)}>
            <div className="modal-content template-modal" onClick={e => e.stopPropagation()}>
              <h3>{selectedTemplate.key.replace(/_/g, ' ')}</h3>
              <div className="template-detail">
                <label>Subject:</label>
                <input value={fillTemplateExample(selectedTemplate.subject)} readOnly />
                <label>Body:</label>
                <textarea value={fillTemplateExample(selectedTemplate.body)} readOnly rows={10} />
                <p className="template-example-note">Shown with example details — Copy gives you the fill-in-yourself version.</p>
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setSelectedTemplate(null)}>Close</button>
                <button className="btn btn-primary" onClick={() => {
                  navigator.clipboard.writeText(selectedTemplate.body);
                  setSelectedTemplate(null);
                }}>Copy Template</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Education Gate Component - Shows when education not complete
  function EducationGate() {
    const modulesCompleted = educationStatus?.modules_completed || 0;
    const totalModules = educationStatus?.total_modules || 36;
    const progressPercent = Math.round((modulesCompleted / totalModules) * 100);

    return (
      <div className="content education-gate" data-testid="education-gate">
        <div className="gate-icon"><GraduationCap size={30} strokeWidth={1.5} /></div>
        <h2>Complete Your Education First</h2>
        <p>
          To access the full CoFitter business tools, you need to complete your 
          Henry-Griffitts Certified Fitter training.
        </p>
        
        <div className="gate-progress">
          <div className="gate-progress-bar">
            <div 
              className="gate-progress-fill" 
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="gate-progress-text">
            {modulesCompleted} of {totalModules} modules completed ({progressPercent}%)
          </div>
        </div>
        
        <div className="gate-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setView("dashboard")}
            data-testid="continue-training-btn"
          >
            Continue Training
          </button>
        </div>
        
        <div className="gate-info">
          <h3>Why complete education first?</h3>
          <ul>
            <li>Master the Precision Fitting Method</li>
            <li>Learn HG product specifications</li>
            <li>Understand sales and customer service best practices</li>
            <li>Earn your official HG Certification</li>
          </ul>
        </div>
      </div>
    );
  }

  // CoFitter Dashboard Component
  function CoPilotDashboard() {
    // Check if education gate should be shown
    const shouldShowGate = educationStatus && 
      !educationStatus.education_completed && 
      !educationStatus.is_exempt;

    if (shouldShowGate) {
      return <EducationGate />;
    }

    return (
      <div className="copilot-content copilot-dashboard" data-testid="copilot-dashboard">
        <div className="copilot-header">
          <h2>Welcome to Henry-Griffitts CoFitter</h2>
          <p>Your AI-powered business assistant</p>
        </div>

        {/* ONE CLICK TO WHO HAS ANSWERED. Admins only — a fitter has no business reading the
            cohort's opt-in position, and the endpoint behind it is admin-gated anyway, so an
            unconditional card would render a permission error on every fitter's dashboard.
            The button opens Admin already on the tracker; it does not land on Overview. */}
        {user?.is_admin && (
          <PilotOptInCard onOpen={() => { setAdminTab("pilot"); setView("admin"); }} />
        )}

        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon"><Users size={26} strokeWidth={1.7} /></div>
            <div className="stat-value">{copilotAnalytics?.total_golfers || 0}</div>
            <div className="stat-label">Total Customers</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><Target size={26} strokeWidth={1.7} /></div>
            <div className="stat-value">{copilotAnalytics?.total_fittings || 0}</div>
            <div className="stat-label">Fittings</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><Calendar size={26} strokeWidth={1.7} /></div>
            <div className="stat-value">{copilotAnalytics?.upcoming_events || 0}</div>
            <div className="stat-label">Upcoming Events</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><MapPin size={26} strokeWidth={1.7} /></div>
            <div className="stat-value">{copilotAnalytics?.partner_facilities || 0}</div>
            <div className="stat-label">Partner Facilities</div>
          </div>
        </div>

        <div className="quick-actions">
          <h3>Quick Actions</h3>
          <div className="actions-grid">
            <button className="action-btn" onClick={() => setCopilotView("randy")}>
              <span className="action-icon"><Sparkles size={22} strokeWidth={1.7} /></span>
              <span>Ask CoFitter</span>
            </button>
            <button className="action-btn" onClick={() => setCopilotView("crm")}>
              <span className="action-icon"><UserPlus size={22} strokeWidth={1.7} /></span>
              <span>Add Customer</span>
            </button>
            <button className="action-btn" onClick={() => setCopilotView("fitting")}>
              <span className="action-icon"><Target size={22} strokeWidth={1.7} /></span>
              <span>New Fitting</span>
            </button>
            <button className="action-btn" onClick={() => setCopilotView("events")}>
              <span className="action-icon"><CalendarPlus size={22} strokeWidth={1.7} /></span>
              <span>Schedule Event</span>
            </button>
          </div>
        </div>

        {copilotAnalytics?.recent_fittings_30d > 0 && (
          <div className="recent-activity">
            <h3>Recent Activity (30 days)</h3>
            <p>{copilotAnalytics.recent_golfers_30d} new customers • {copilotAnalytics.recent_fittings_30d} fittings</p>
            <p>Conversion Rate: {copilotAnalytics.conversion_rate}%</p>
          </div>
        )}
      </div>
    );
  }

  // Dashboard Component
  function Dashboard() {
    const nextUp = MODULES.find(m => !isModuleCompleted(m.id) && !isModuleLocked(m.id));
    return (
      <div className="content" data-testid="dashboard">
        {/* Announcements Banner */}
        {announcements.length > 0 && (
          <div className="announcements-banner" data-testid="announcements-banner">
            {announcements.slice(0, 3).map(ann => (
              <div key={ann.announcement_id} className={`announcement-item priority-${ann.priority}`}>
                <div className="announcement-icon">
                  {ann.priority === "urgent" ? <AlertTriangle size={18} strokeWidth={1.8} /> : ann.priority === "important" ? <Megaphone size={18} strokeWidth={1.8} /> : <Bell size={18} strokeWidth={1.8} />}
                </div>
                <div className="announcement-content">
                  <strong>{ann.title}</strong>
                  <p>{ann.content}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="dashboard-hero">
          <h2>Welcome back,<br /><span>{user.name?.split(" ")[0]}</span></h2>
          <p>Your certified fitter hub. Access business tools, training modules, and community resources.</p>
          {nextUp && !allLegacyModulesComplete && (
            <button className="btn btn-primary" onClick={() => openModule(nextUp)} data-testid="continue-btn">
              {completedModules.length === 0 ? "▶ Start Module 1" : `▶ Continue: Module ${nextUp.id}`}
            </button>
          )}
          {allLegacyModulesComplete && (   /* audit #6: only ACTUAL completion unlocks the certificate */
            <button className="btn btn-success" onClick={() => setView("certificate")} data-testid="view-cert-btn">
              View Your Certificate
            </button>
          )}
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{completedModules.length}</div>
            <div className="stat-label">Modules Complete</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{totalProgress}%</div>
            <div className="stat-label">Course Progress</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{Object.keys(quizScores).length}</div>
            <div className="stat-label">Quizzes Passed</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {Object.values(quizScores).length ? Math.round(Object.values(quizScores).reduce((a, b) => a + b, 0) / Object.values(quizScores).length) : "--"}%
            </div>
            <div className="stat-label">Avg. Quiz Score</div>
          </div>
        </div>
        <div className="section-title">All Modules</div>
        <p className="section-sub">Complete each module in order to unlock the next.</p>
        <div className="modules-grid">
          {MODULES.map(mod => (
            <div
              key={mod.id}
              className={`module-card ${isModuleLocked(mod.id) ? "locked" : ""} ${isModuleCompleted(mod.id) ? "completed" : ""}`}
              onClick={() => !isModuleLocked(mod.id) && openModule(mod)}
              data-testid={`module-card-${mod.id}`}
            >
              <div className="module-card-num">Module {mod.id}</div>
              <div className="module-card-title">{mod.title}</div>
              <div className="module-card-cat">
                <span className="cat-badge">{getCategoryIcon(mod.category)}</span> {mod.category}
              </div>
              <div className="module-card-meta">
                <span className="module-card-slides">{mod.slides.length} slides</span>
                <span className="module-card-status">
                  {isModuleLocked(mod.id) ? <span className="status-locked"><Lock size={11} strokeWidth={1.8} /> Locked</span>
                    : isModuleCompleted(mod.id) ? <span className="status-complete">✓ {quizScores[mod.id]}%</span>
                      : <span className="status-start">→ Start</span>}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Slide Viewer Component
  function SlideViewer() {
    const slide = currentModule.slides[currentSlide];
    const isLast = currentSlide === currentModule.slides.length - 1;
    const slideProgress = ((currentSlide + 1) / currentModule.slides.length) * 100;
    const [showComments, setShowComments] = useState(false);
    const [moduleVideos, setModuleVideos] = useState({});
    const [showVideo, setShowVideo] = useState(false);

    useEffect(() => {
      loadModuleVideos();
    }, []);

    async function loadModuleVideos() {
      try {
        const res = await fetch(`${API_URL}/api/module-videos`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setModuleVideos(data.videos || {});
        }
      } catch (err) {
        console.error("Failed to load module videos:", err);
      }
    }

    const currentVideo = moduleVideos[currentModule.id];
    
    return (
      <div className="content slide-viewer" data-testid="slide-viewer">
        <div className="slide-header">
          <div className="slide-module-title">Module {currentModule.id} — {currentModule.title}</div>
          <div className="slide-counter">Slide {currentSlide + 1} of {currentModule.slides.length}</div>
          <div className="slide-progress">
            <div className="slide-progress-fill" style={{ width: `${slideProgress}%` }} />
          </div>
        </div>

        {/* Module Video Section */}
        {currentVideo && currentSlide === 0 && (
          <div className="module-video-section" data-testid="module-video-section">
            {!showVideo ? (
              <div className="video-preview" onClick={() => setShowVideo(true)}>
                <div className="video-play-overlay">
                  <div className="play-icon">▶</div>
                  <span>Watch AI-Generated Overview</span>
                </div>
              </div>
            ) : (
              <div className="video-player">
                <video 
                  controls 
                  autoPlay 
                  src={currentVideo.video_url}
                  className="module-video"
                  data-testid="module-video-player"
                >
                  Your browser does not support the video tag.
                </video>
                <button className="btn btn-text video-close" onClick={() => setShowVideo(false)}>
                  Close Video
                </button>
              </div>
            )}
          </div>
        )}

        <div className="slide-card">
          <div className="slide-image">
            <span className="slide-cat-icon">{getCategoryIcon(currentModule.category)}</span>
          </div>
          <div className="slide-body">
            <div className="slide-title">{slide.title}</div>
            <div className="slide-content">{formatContent(slide.content)}</div>
          </div>
        </div>
        <div className="slide-nav">
          <button
            className="btn btn-secondary"
            disabled={currentSlide === 0}
            onClick={() => setCurrentSlide(s => s - 1)}
            data-testid="prev-slide-btn"
          >← Previous</button>
          <div className="slide-nav-center">
            <span className="slide-indicator">{currentSlide + 1} / {currentModule.slides.length}</span>
            <button 
              className="btn btn-text"
              onClick={() => setShowComments(!showComments)}
              data-testid="toggle-comments-btn"
            >
              <MessagesSquare size={13} strokeWidth={1.8} /> {comments.length} Comments
            </button>
          </div>
          {isLast ? (
            currentModule.quiz.length > 0 ? (
              <button className="btn btn-primary" onClick={goToQuiz} data-testid="take-quiz-btn">
                Take Quiz →
              </button>
            ) : (
              <button className="btn btn-primary" onClick={() => {
                const newCompleted = completedModules.includes(currentModule.id) ? completedModules : [...completedModules, currentModule.id];
                setCompletedModules(newCompleted);
                saveProgress(newCompleted, quizScores);
                nextModule();
              }} data-testid="next-module-btn">
                Next Module →
              </button>
            )
          ) : (
            <button className="btn btn-primary" onClick={() => setCurrentSlide(s => s + 1)} data-testid="next-slide-btn">
              Next →
            </button>
          )}
        </div>
        
        {showComments && (
          <div className="comments-section" data-testid="comments-section">
            <div className="comments-header">
              <h3>Discussion</h3>
              <span className="comments-count">{comments.length} comments</span>
            </div>
            <div className="comment-form">
              <textarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Ask a question or share your thoughts..."
                className="comment-input"
                data-testid="comment-input"
              />
              <button 
                className="btn btn-primary"
                disabled={!newComment.trim()}
                onClick={() => postComment(currentModule.id, newComment)}
                data-testid="post-comment-btn"
              >
                Post Comment
              </button>
            </div>
            <div className="comments-list">
              {comments.map(comment => (
                <div key={comment.comment_id} className="comment-item">
                  <div className="comment-avatar">
                    {comment.user_picture ? (
                      <img src={comment.user_picture} alt="" />
                    ) : (
                      <span>{comment.user_name?.charAt(0)?.toUpperCase()}</span>
                    )}
                  </div>
                  <div className="comment-body">
                    <div className="comment-meta">
                      <span className="comment-author">{comment.user_name}</span>
                      <span className="comment-date">{new Date(comment.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="comment-content">{comment.content}</div>
                    <div className="comment-actions">
                      <button 
                        className="comment-reply-btn"
                        onClick={() => setReplyingTo(replyingTo === comment.comment_id ? null : comment.comment_id)}
                      >
                        Reply
                      </button>
                      {(comment.user_id === user?.user_id || user?.is_admin) && (
                        <button className="comment-delete-btn" onClick={() => deleteComment(comment.comment_id)}>
                          Delete
                        </button>
                      )}
                    </div>
                    {replyingTo === comment.comment_id && (
                      <div className="reply-form">
                        <textarea
                          placeholder="Write a reply..."
                          className="comment-input reply-input"
                          id={`reply-${comment.comment_id}`}
                        />
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            const input = document.getElementById(`reply-${comment.comment_id}`);
                            if (input.value.trim()) {
                              postComment(currentModule.id, input.value, comment.comment_id);
                            }
                          }}
                        >
                          Reply
                        </button>
                      </div>
                    )}
                    {comment.replies?.length > 0 && (
                      <div className="comment-replies">
                        {comment.replies.map(reply => (
                          <div key={reply.comment_id} className="comment-item reply">
                            <div className="comment-avatar small">
                              {reply.user_picture ? (
                                <img src={reply.user_picture} alt="" />
                              ) : (
                                <span>{reply.user_name?.charAt(0)?.toUpperCase()}</span>
                              )}
                            </div>
                            <div className="comment-body">
                              <div className="comment-meta">
                                <span className="comment-author">{reply.user_name}</span>
                                <span className="comment-date">{new Date(reply.created_at).toLocaleDateString()}</span>
                              </div>
                              <div className="comment-content">{reply.content}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <div className="no-comments">No comments yet. Be the first to ask a question!</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Quiz Component
  function Quiz() {
    const q = currentModule.quiz;
    const passed = quizScore >= PASS_SCORE;
    const isNextLast = currentModule.id === MODULES.length;

    if (quizSubmitted) {
      return (
        <div className="content quiz-container" data-testid="quiz-results">
          <div className="quiz-result">
            <div className="quiz-result-icon">{passed ? <Trophy size={34} strokeWidth={1.6} /> : <BookOpen size={34} strokeWidth={1.6} />}</div>
            <div className="quiz-result-score">{quizScore}%</div>
            <div className="quiz-result-label">
              {Object.values(quizAnswers).filter((a, i) => a === q[i]?.answer).length} of {q.length} correct
            </div>
            <div className="quiz-result-message">
              {passed
                ? `Excellent work. You've passed Module ${currentModule.id} — ${currentModule.title}.`
                : `You need ${PASS_SCORE}% to pass. Review the slides and try again.`}
            </div>
            <div className="quiz-actions">
              {!passed && (
                <>
                  <button className="btn btn-secondary" onClick={() => setView("module")} data-testid="review-slides-btn">← Review Slides</button>
                  <button className="btn btn-primary" onClick={retakeQuiz} data-testid="retake-quiz-btn">Retake Quiz</button>
                </>
              )}
              {passed && (
                <>
                  <button className="btn btn-secondary" onClick={() => setView("dashboard")} data-testid="back-dashboard-btn">← Dashboard</button>
                  {isNextLast && allLegacyModulesComplete
                    ? <button className="btn btn-success" onClick={() => setView("certificate")} data-testid="get-cert-btn">Get Certificate</button>
                    : <button className="btn btn-primary" onClick={nextModule} data-testid="next-module-btn">Next Module →</button>
                  }
                </>
              )}
            </div>
            <div className="divider" />
            <div className="quiz-review">
              {q.map((item, i) => {
                const userAns = quizAnswers[i];
                const correct = userAns === item.answer;
                return (
                  <div key={i} className="quiz-review-item">
                    <div className={`quiz-review-status ${correct ? 'correct' : 'incorrect'}`}>
                      {correct ? "✓ Correct" : "✗ Incorrect"} — Q{i + 1}
                    </div>
                    <div className="quiz-review-question">{item.q}</div>
                    {!correct && (
                      <div className="quiz-review-answer">
                        Correct answer: {item.options[item.answer]}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="content quiz-container" data-testid="quiz">
        <div className="quiz-header">
          <div className="quiz-title">Module {currentModule.id} Quiz</div>
          <div className="quiz-subtitle">{currentModule.title} — Answer all questions and submit. Pass score: {PASS_SCORE}%</div>
        </div>
        {q.map((item, i) => (
          <div key={i} className="quiz-question-card">
            <div className="quiz-q-num">Question {i + 1} of {q.length}</div>
            <div className="quiz-q-text">{item.q}</div>
            <div className="quiz-options">
              {item.options.map((opt, j) => (
                <div
                  key={j}
                  className={`quiz-option ${quizAnswers[i] === j ? "selected" : ""}`}
                  onClick={() => !quizSubmitted && setQuizAnswers(prev => ({ ...prev, [i]: j }))}
                  data-testid={`quiz-option-${i}-${j}`}
                >
                  <div className="quiz-option-letter">{["A", "B", "C", "D"][j]}</div>
                  {opt}
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="quiz-footer">
          <button className="btn btn-secondary" onClick={() => setView("module")} data-testid="back-slides-btn">← Back to Slides</button>
          <button
            className="btn btn-primary"
            disabled={Object.keys(quizAnswers).length < q.length}
            onClick={submitQuiz}
            data-testid="submit-quiz-btn"
          >Submit Quiz</button>
        </div>
      </div>
    );
  }

  // Certificate Component
  function Certificate() {
    // The legacy certificate's helpers went with it: today's date as an issue date, an
    // average of RETIRED module quiz scores, and a download from the legacy endpoint. The
    // real credential is issued with its own number and downloads through the block below.

    // PHASE 4 (2026-08-11): THE REAL CREDENTIAL. Below this block is the LEGACY certificate —
    // "all 36 modules", today's date, an average of legacy quiz scores, downloaded from the
    // legacy endpoint. Making the nav item visible to a Track-A-complete fitter without this
    // would have pointed a newly-reachable screen at the wrong certificate.
    //
    // What a holder sees here is the record as ISSUED: the certificate number, the level, the
    // issue and expiry dates, the blueprint version they were examined against, and the
    // standard it was awarded against (Henry-Griffitts's own; the "PROVISIONAL" qualifier went on 2026-10-02).
    const hgCert = hgCertification?.certificate;
    if (hgCertification?.eligible) {
      const issued = Boolean(hgCert?.issued_at);
      const status = hgCertification.certificate_status;
      // earned / queued / issued / revoked -- each says only what is true (lib/certificateState.js).
      const state = certificateState(hgCertification);
      const fmt = (iso) => iso
        ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
        : "—";
      return (
        <div className="content" data-testid="hg-certificate">
          <div className="certificate">
            <img src="/images/hg-logo.png" alt="Henry-Griffitts" className="cert-logo" />
            <div className="cert-label">Henry-Griffitts Golf</div>
            <div className="cert-title">
              {state.title}
            </div>
            <div className="cert-subtitle">The Fitting Method — Track A</div>
            <div className="divider" />
            <p className="cert-award-text">This certifies that</p>
            <div className="cert-name">{hgCert?.holder_name || user.name}</div>
            <div className="cert-body">
              has completed every chapter of the Henry-Griffitts fitting curriculum and passed the
              final examination.
            </div>
            {state.message && (
              <div className="cert-body" data-testid={`hg-certificate-${state.key}`}>
                {state.message}
              </div>
            )}
            <div className="cert-stats">
              <div className="cert-stat">
                <div className="cert-stat-value">{hgCertification.chapters_complete}/{hgCertification.chapters}</div>
                <div className="cert-stat-label">Chapters</div>
              </div>
              <div className="cert-stat">
                <div className="cert-stat-value">
                  {hgCertification.final_exam_score_pct != null
                    ? `${Math.round(hgCertification.final_exam_score_pct)}%` : "—"}
                </div>
                <div className="cert-stat-label">Final Exam</div>
              </div>
              <div className="cert-stat">
                <div className="cert-stat-value">{state.statusLabel}</div>
                <div className="cert-stat-label">Status</div>
              </div>
            </div>
            <div className="divider" />
            <div className="cert-body" style={{ textAlign: "left", fontSize: "0.85rem" }}>
              <div>Certificate number: <strong>{hgCert?.certificate_number || "—"}</strong></div>
              <div>Issued: <strong>{fmt(hgCert?.issued_at)}</strong></div>
              <div>Expires: <strong>{fmt(hgCert?.expires_at)}</strong></div>
              <div>Blueprint version: <strong>{hgCert?.blueprint_version || hgCertification.blueprint_version}</strong></div>
              <div style={{ marginTop: "0.75rem", opacity: 0.75 }}>
                {hgCert?.pass_mark_note_at_issue || hgCertification.pass_mark_note}
              </div>
            </div>
            {issued && status !== "revoked" && (
              <button
                className="btn btn-primary"
                data-testid="hg-cert-download"
                onClick={() => window.open(`${API_URL}/api/education/certification/certificate.pdf`, "_blank")}
              >
                Download PDF
              </button>
            )}
          </div>
        </div>
      );
    }

    // THE LEGACY CERTIFICATE IS GONE (2026-09-01). This fallback rendered whenever the real
    // credential was not yet earned, and it awarded one anyway: "has successfully completed
    // all 36 modules", a "36 Modules" stat, and today's date as the issue date. 36 is the
    // RETIRED curriculum's count, and nobody has completed it — the live course is chapters
    // and lessons, and the only certificate that means anything is the one issued above,
    // against a blueprint, with a number.
    //
    // Not earned is a thing worth saying plainly, so it says it.
    return (
      <div className="content" data-testid="certificate">
        <div className="section-title">Certificate</div>
        <p className="section-sub">
          Your Henry-Griffitts Certified Fitter certificate is issued once you have completed
          every chapter of the course and passed the final exam. It carries a certificate
          number and can be verified by anyone you show it to.
        </p>
        <div className="admin-card" style={{ marginTop: "1rem" }}>
          <p className="section-sub" style={{ margin: 0 }}>
            You haven't finished the course yet — open <b>HG Education</b> to pick up where
            you left off.
          </p>
        </div>
      </div>
    );
  }

  // Admin Analytics Component

  // Settings Component

  // Main Render
  return (
    <div className="hg-app">
      <Sidebar
        user={user} view={view} setView={setView}
        copilotView={copilotView} setCopilotView={setCopilotView}
        sidebarOpen={sidebarOpen} totalProgress={totalProgress}
        hgCertification={hgCertification} educationStatus={educationStatus}
        completedModules={completedModules}
        allLegacyModulesComplete={allLegacyModulesComplete}
        currentModule={currentModule} openModule={openModule}
        isModuleLocked={isModuleLocked} isModuleCompleted={isModuleCompleted}
        closeSidebarOnMobile={closeSidebarOnMobile}
        handleLogout={handleLogout}
      />
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}
      <div className={`main ${sidebarOpen ? '' : 'expanded'}`}>
        <TopBar
          user={user} view={view} copilotView={copilotView}
          sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
          currentModule={currentModule} currentSlide={currentSlide}
          completedModules={completedModules}
        />
        {/* ERROR BOUNDARY AROUND THE VIEW AREA ONLY, and the placement is the point (2026-08-13).
            Wrapping the whole app would mean a bad customer record still cost you the navigation
            — you would be stranded on an error page with no way out. Wrapping HERE leaves the
            Sidebar and TopBar alive, so a failed view is a panel you can navigate away from.
            `resetKey` clears the error when the view actually changes, so navigating away and
            back is a genuine recovery rather than a stuck screen. */}
        {/* WHAT IS WAITING FOR HIM (Randall, 2026-10-05): "Tell him when one is waiting, rather than him having to
            open the PGA tab." Every page, for an admin; the server says whether this account is the approver. */}
        {user?.is_admin && (
          <SubmissionsWaitingBanner onOpen={() => { setAdminTab("pga"); setView("admin"); }} />
        )}
        <ErrorBoundary label={`${view}:${copilotView}`} resetKey={`${view}:${copilotView}`}>
        {/* PHASE 4 section 0a: gate the RENDER, not just the nav entry. The legacy dashboard's
            only entry point is admin-only, but the view itself was ungated — so if `view` were
            ever set to "education" by any other path, a fitter would see the retired
            curriculum. Defence in depth on a surface that is being decommissioned. */}
        {/* THE REFUSAL IS RENDERED DELIBERATELY, not reached by falling through. The effect
            above redirects, but a render between the state change and the effect would flash
            the Hub; this makes that impossible. */}
        {isEducationOnly(user) && !EDUCATION_ONLY_VIEWS.includes(view) ? (
          <EducationOnlyNotice onBack={() => setView("hg-education")} />
        ) : (
        <>
        {view === "education" && user?.is_admin && !LEGACY_MODULES_VOIDED && <Dashboard />}
        {view === "hg-education" && <Education user={user} />}
        {view === "module" && currentModule && !LEGACY_MODULES_VOIDED && <SlideViewer />}
        {view === "quiz" && currentModule && !LEGACY_MODULES_VOIDED && <Quiz />}
        {view === "certificate" && <Certificate />}
        {view === "admin" && (
          <AdminAnalytics user={user} adminData={adminData} adminLoading={adminLoading}
                          loadAdminData={loadAdminData} loadUserDetails={loadUserDetails}
                          selectedUser={selectedUser} setSelectedUser={setSelectedUser}
                          groups={groups} announcements={announcements}
                          loadAnnouncements={loadAnnouncements}
                          activeTab={adminTab} setActiveTab={setAdminTab} />)}
        {view === "settings" && (
          <Settings user={user} completedModules={completedModules} quizScores={quizScores}
                    handleLogout={handleLogout} resetProgress={resetProgress} />)}
        {view === "copilot" && copilotView === "dashboard" && <CoPilotDashboard />}
        {view === "copilot" && copilotView === "randy" && <RandyChat messages={chatMessages} />}
        {view === "copilot" && copilotView === "crm" && (
          <CRMView view={view} golfers={golfers} fittings={fittings}
                   setSelectedGolfer={setSelectedGolfer} />)}
        {view === "copilot" && copilotView === "fitting" && (
          <FittingView view={view} golfers={golfers} fittings={fittings} />)}
        {view === "copilot" && copilotView === "events" && (
          <EventsView view={view} events={events} />)}
        {view === "copilot" && copilotView === "territory" && (
          <TerritoryView view={view} territory={territory} />)}
        {view === "copilot" && copilotView === "marketing" && <MarketingView />}
        {view === "copilot" && copilotView === "brandmarketing" && (
          <BrandMarketing user={user} onOpenGuidelines={() => setCopilotView("brandguidelines")} />)}
        {view === "copilot" && copilotView === "brandguidelines" && (
          <BrandGuidelines onBack={() => setCopilotView("brandmarketing")} />)}
        {view === "copilot" && copilotView === "forum" && <CoachRoundTable user={user} />}
        {view === "copilot" && copilotView === "fittingsystem" && <FittingSystemView user={user} />}
        {view === "copilot" && copilotView === "license" && <LicenseFeeView user={user} />}
        {view === "copilot" && copilotView === "payments" && <PaymentsView user={user} />}
        </>
        )}
        </ErrorBoundary>
      </div>
    </div>
  );
}
